import { useState, useEffect, useMemo, useRef } from "react";

// =====================================================================
// SEO FOUNDATION
// =====================================================================
//
// Real production domain, provided directly by the project owner.
// No trailing slash -- canonicalPath values already start with "/".
const PRODUCTION_DOMAIN = "https://www.oasiscopilot.com";

// Sets document.title, meta description, canonical link, Open Graph and
// Twitter tags, and (optionally) JSON-LD structured data, entirely from
// React via useEffect. This exists because I do not have this project's
// actual public/index.html to edit directly -- static meta tags in the
// real index.html would be the stronger, more crawler-reliable choice,
// since JS-injected tags only exist after the page's JavaScript runs.
// Modern Google crawling does execute JavaScript for indexing purposes,
// but this is a real trade-off worth disclosing, not treating as
// equivalent to static HTML.
function useDocumentSEO({ title, description, canonicalPath, noindex, jsonLd }) {
  useEffect(() => {
    if (title) document.title = title;

    function setMeta(name, content, attr = "name") {
      if (!content) return;
      let tag = document.querySelector(`meta[${attr}="${name}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    }

    if (description) setMeta("description", description);
    setMeta("robots", noindex ? "noindex, nofollow" : "index, follow");
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", "website", "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);

    const canonicalUrl = canonicalPath ? `${PRODUCTION_DOMAIN}${canonicalPath}` : null;
    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonicalUrl);
      setMeta("og:url", canonicalUrl, "property");
    }

    let ldScript = document.getElementById("seo-json-ld");
    if (jsonLd) {
      if (!ldScript) {
        ldScript = document.createElement("script");
        ldScript.type = "application/ld+json";
        ldScript.id = "seo-json-ld";
        document.head.appendChild(ldScript);
      }
      ldScript.textContent = JSON.stringify(jsonLd);
    } else if (ldScript) {
      ldScript.remove();
    }
  }, [title, description, canonicalPath, noindex, jsonLd]);
}

// Structured data builders. Every value here is either a fixed product
// fact (name, category) or pulled from pricing already visibly displayed
// on the pricing section of this same page -- nothing is fabricated.
// Deliberately omits aggregateRating, review, and any customer-count
// claim, since none of those are established anywhere in this project.
function buildOrganizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "OasisCopilot",
    url: PRODUCTION_DOMAIN
  };
}

function buildSoftwareApplicationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "OasisCopilot",
    applicationCategory: "BusinessApplication",
    description:
      "OasisCopilot helps clinicians and QA teams identify documentation issues, clarify missing information, resolve problems, generate copy-ready content, recheck the assessment, and complete a final visit QA review for home health OASIS-E2 documentation.",
    offers: [
      { "@type": "Offer", name: "Clinician", price: "59", priceCurrency: "USD" },
      { "@type": "Offer", name: "Agency Team", price: "149", priceCurrency: "USD" },
      { "@type": "Offer", name: "Agency Team Annual", price: "1999", priceCurrency: "USD" }
    ]
  };
}

function buildWebSiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "OasisCopilot",
    url: PRODUCTION_DOMAIN
  };
}

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_BASE ||
  "http://localhost:5050";

// === Stripe LIVE Payment Links ===
// === Brand assets ===
// Embedded as data URIs rather than imported file paths: no public/ folder
// or existing asset pipeline was visible in the project as provided, so
// this is the most self-contained way to ship the logo without assuming
// build-tooling wiring that couldn't be verified.
const LOGO_LOCKUP_DATA_URI = "data:image/png;base64,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";
const LOGO_BADGE_DATA_URI = "data:image/png;base64,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";
const LOGO_FAVICON_DATA_URI = "data:image/png;base64,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";

const STRIPE_INDIVIDUAL_URL = "https://buy.stripe.com/8x2eVc4YX86v0jkbV9a7C08";
const STRIPE_AGENCY_MONTHLY_URL = "https://buy.stripe.com/8x28wO2QP1I70jk5wLa7C0b";
const STRIPE_AGENCY_ANNUAL_URL = "https://buy.stripe.com/4gM28qcrpdqP6HI1gva7C0a";

// === Demo (Loom) ===
const DEMO_URL = "https://www.loom.com/share/ee0deaf5b3dd4d60863eff7f095ee657";

// === Enterprise landing-page color system ===
// Scoped entirely to the public landing page. Does not replace or interact
// with the ACCENT_BLUE/TEXT/MUTED/BORDER constants used inside App() for
// the authenticated workspace, which remain completely untouched.
//
// Deliberately refined beyond the starting palette: navy deepened toward
// near-black for more executive authority, blue deepened/desaturated so it
// reads as considered rather than bright-startup, teal reserved strictly
// for small accents. ENT_NAVY_DEEP and ENT_SURFACE are new tokens added to
// support genuine section-to-section contrast (dark hero -> white product
// area -> light-blue-gray informational area -> dark trust/final CTA)
// without relying on bordered cards for every piece of content.
const ENT_NAVY = "#0A1B2E";
const ENT_NAVY_DEEP = "#04101C";
const ENT_BLUE = "#12588F";
const ENT_TEAL = "#0E9F9A";
const ENT_BG = "#F7F9FB";
const ENT_SURFACE = "#EEF2F6";
const ENT_TEXT = "#0F2237";
const ENT_MUTED = "#57697D";
const ENT_BORDER = "#E1E7ED";

// Support + Privacy
const SUPPORT_EMAIL = "support@oasiscopilot.com";
const PRIVACY_PATH = "/privacy.html";

// Storage keys
const SESSION_KEY = "oasis_org_code_session";
const LOCAL_KEY = "oasis_org_code_local";

// Session-only draft recovery. Stores ONLY the clinician's typed inputs
// (assessment type, notes, intake fields, OASIS responses text, template
// choice) -- never AI-generated results, which can be regenerated on
// demand. Uses sessionStorage, not localStorage: cleared automatically
// when the browser tab closes, and explicitly cleared by this app on
// sign-out or when the user clicks Clear or Discard on the restore banner.
// No PHI persists beyond the active browser session.
const DRAFT_STORAGE_KEY = "oasis_draft_session_v1";

// === Live Review (auto-review) config ===
// Time of typing inactivity before an automatic review is triggered.
const LIVE_REVIEW_DEBOUNCE_MS = 2500;
// Minimum combined content length before auto-review is allowed to fire,
// to avoid burning API calls on trivially short input.
const LIVE_REVIEW_MIN_CONTENT_LENGTH = 60;

const TEMPLATE_GENERAL = `Documentation Template (De-identified) — General (Recommended)

Reason for visit / chief concern:
-

Vitals:
- Temp:
- BP:
- HR:
- RR:
- SpO₂ (room air / O₂ flow rate if applicable):

Respiratory:
- Cough (duration, productive? sputum?):
- Dyspnea (rest / exertion):
- Lung sounds:
- O₂ therapy (none / device / flow rate):

Nutrition/Hydration:
- Appetite:
- Fluid intake:
- Weight (current / recent change):

Functional status (ADLs):
- Ambulation/locomotion (device, assistance, safety):
- Transfers:
- Bathing:
- Dressing:
- Toileting:
- Eating/feeding:

Cognition/behavior:
- Orientation:
- Ability to follow instructions / safety awareness:

Medications:
- Medication reconciliation completed? (Y/N)
- Adherence concerns:

Skin/Integumentary:
- Skin condition / wounds (if any):

Homebound status (if applicable):
- Reason / qualifying factors:

Caregiver/support:
- Who is available and what assistance is provided:

Notifications / orders:
- Physician notified? (Y/N)
- Current orders / monitoring / escalation parameters:
`;

const TEMPLATE_RESPIRATORY = `Documentation Template (De-identified) — Respiratory Focus

Vitals (include SpO₂):
- Temp:
- BP:
- HR:
- RR:
- SpO₂ at rest:
- SpO₂ with activity:
- O₂ therapy (device / flow rate):

Cough details:
- Productive? (Y/N)
- Sputum characteristics:
- Frequency/duration:

Dyspnea:
- At rest? (Y/N)
- With exertion? (Y/N)

Assessment:
- Lung sounds:
- Signs of respiratory distress:

Physician notification / orders:
- MD notified? (Y/N)
- Orders or escalation criteria:
`;

const TEMPLATE_WOUND = `Documentation Template (De-identified) — Wound / Skin Focus

Skin assessment:
- Overall skin integrity:

If wound present:
- Location:
- Type:
- Size (L x W x D):
- Drainage:
- Wound bed:
- Periwound condition:
- Pain:
- Signs of infection:

Care plan:
- Dressing type/frequency:
- Physician notified? (Y/N)
`;

const TEMPLATE_FUNCTIONAL = `Documentation Template (De-identified) — Functional / ADL Focus

Baseline vs current function:
- Prior level of function:
- Current limitations:

Ambulation:
- Device:
- Assistance level:
- Safety concerns:

Transfers:
- Bed/chair:
- Toilet:
- Shower/tub:

ADLs:
- Bathing:
- Dressing:
- Toileting:
- Eating:

Cognition/safety:
- Orientation:
- Safety awareness:
`;

const TEMPLATE_OPTIONS = [
  { value: "general", label: "General (recommended)", text: TEMPLATE_GENERAL },
  { value: "respiratory", label: "Respiratory focus", text: TEMPLATE_RESPIRATORY },
  { value: "wound", label: "Wound / Skin focus", text: TEMPLATE_WOUND },
  { value: "functional", label: "Functional / ADL focus", text: TEMPLATE_FUNCTIONAL }
];

const INTAKE_FIELDS = [
  { key: "ambulation", label: "Ambulation / locomotion", placeholder: "Device, distance, supervision, safety" },
  { key: "transfers", label: "Transfers", placeholder: "Bed/chair/toilet transfer ability and assistance" },
  { key: "bathing", label: "Bathing", placeholder: "Independent, setup, supervision, assistance" },
  { key: "dressing", label: "Dressing", placeholder: "Upper/lower body, supervision, assistance" },
  { key: "toileting", label: "Toileting hygiene", placeholder: "Toilet use, hygiene, continence support" },
  { key: "feeding", label: "Feeding / eating", placeholder: "Independent, setup, cueing, assistance" },
  { key: "dyspnea", label: "Dyspnea / shortness of breath", placeholder: "At rest vs exertion, trigger, severity" },
  { key: "medications", label: "Medication management", placeholder: "Setup, reminders, independent, assistance" },
  { key: "caregiver", label: "Caregiver support", placeholder: "Who helps, tasks, frequency, availability" },
  { key: "homebound", label: "Homebound status", placeholder: "Why leaving home is difficult or unsafe" }
];

// Smart Resolution's per-issue state machine values.
const RESOLUTION_STATE = {
  UNRESOLVED: "unresolved",
  AWAITING_INPUT: "awaiting_clinician_input",
  READY_TO_INSERT: "ready_to_insert",
  INSERTED: "inserted",
  RECHECKING: "rechecking",
  RESOLVED: "resolved",
  STILL_NEEDS_ATTENTION: "still_needs_attention"
};

function normalizeCode(code) {
  return String(code || "").trim().toUpperCase();
}

function getStoredCode() {
  const local = localStorage.getItem(LOCAL_KEY);
  if (local) return local;
  const session = sessionStorage.getItem(SESSION_KEY);
  if (session) return session;
  return "";
}

function storeCode(code, remember) {
  const norm = normalizeCode(code);
  if (!norm) return;

  localStorage.removeItem(LOCAL_KEY);
  sessionStorage.removeItem(SESSION_KEY);

  if (remember) localStorage.setItem(LOCAL_KEY, norm);
  else sessionStorage.setItem(SESSION_KEY, norm);
}

function clearStoredCode() {
  localStorage.removeItem(LOCAL_KEY);
  sessionStorage.removeItem(SESSION_KEY);
}

function saveDraftToSession(draft) {
  try {
    sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // sessionStorage may be unavailable (e.g. private browsing); draft
    // recovery is a convenience, not a requirement, so fail silently.
  }
}

function loadDraftFromSession() {
  try {
    const raw = sessionStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function clearDraftFromSession() {
  try {
    sessionStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch {
    // no-op
  }
}

function parseQaResponses(input) {
  const raw = String(input || "").trim();
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const normalized = {};
      for (const [key, value] of Object.entries(parsed)) {
        const code = String(key || "").trim().toUpperCase();
        const cleanValue = String(value ?? "").trim();
        if (/^M\d{4}$/.test(code) && cleanValue) {
          normalized[code] = cleanValue;
        }
      }
      return Object.keys(normalized).length ? normalized : null;
    }
  } catch {
    // continue
  }

  const result = {};
  const addPair = (code, value) => {
    const item = String(code || "").trim().toUpperCase();
    const cleanValue = String(value || "").trim().replace(/^[:\-–—]\s*/, "");
    if (!/^M\d{4}$/.test(item)) return;
    if (!cleanValue) return;
    result[item] = cleanValue;
  };

  const pairRegex =
    /"?\b(M\d{4})\b"?\s*:\s*"([^"]+)"|"?\b(M\d{4})\b"?\s*:\s*([^,\n]+)|\b(M\d{4})\b\s*[-:]\s*([^\n,]+)|\b(M\d{4})\b\s+([^\n,]+)/gi;

  let match;
  while ((match = pairRegex.exec(raw)) !== null) {
    const code = match[1] || match[3] || match[5] || match[7] || "";
    const value = match[2] || match[4] || match[6] || match[8] || "";
    addPair(code, value);
  }

  if (Object.keys(result).length) return result;

  const lines = raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  for (let line of lines) {
    line = line.replace(/\s+/g, " ").trim();
    const codeMatch = line.match(/\b(M\d{4})\b/i);
    if (!codeMatch) continue;
    const item = codeMatch[1].toUpperCase();
    let value = line.replace(codeMatch[0], "").trim();
    value = value.replace(/^[:\-–—]/, "").trim();
    if (!value) continue;
    result[item] = value;
  }

  return Object.keys(result).length ? result : null;
}

function getSubmissionStatusStyle(status) {
  const normalized = String(status || "").toUpperCase();

  if (normalized === "DO_NOT_SUBMIT") {
    return {
      label: "🔴 DO NOT SUBMIT",
      text: "#991b1b",
      bg: "#fef2f2",
      border: "#fecaca"
    };
  }

  if (normalized === "REVIEW") {
    return {
      label: "🟡 REVIEW BEFORE SUBMISSION",
      text: "#92400e",
      bg: "#fffbeb",
      border: "#fde68a"
    };
  }

  return {
    label: "🟢 READY",
    text: "#065f46",
    bg: "#ecfdf5",
    border: "#a7f3d0"
  };
}

function getSubmissionHeadline(status) {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "DO_NOT_SUBMIT") return "Fix before submission";
  if (normalized === "REVIEW") return "Review before submission";
  return "Ready to move forward";
}

function getTopIssuesTitle(issues) {
  const list = Array.isArray(issues) ? issues : [];
  const hasBlocking = list.some((issue) => issue?.severity === "blocking");
  return hasBlocking ? "Top priority issues" : "Top documentation items to review";
}

function normalizeSupportLevel(value) {
  const text = String(value || "").trim().toLowerCase();
  if (!text) return "";
  if (text === "directly supported") return "supported";
  if (text === "not supported") return "unsupported";
  if (text === "mismatch" || text === "mismatched") return "conflicting";
  return text;
}

function getQaSummaryCounts(qaSummary = {}) {
  return {
    totalItems: qaSummary.totalItems ?? 0,
    supported: qaSummary.supported ?? 0,
    partiallySupported: qaSummary.partiallySupported ?? 0,
    unsupported: qaSummary.unsupported ?? 0,
    conflicting: qaSummary.conflicting ?? qaSummary.mismatched ?? 0
  };
}

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function downloadTxt(filename, text) {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function formatValue(v) {
  return typeof v === "string" ? v.trim() : "";
}

function buildStructuredIntakeText(intake) {
  const lines = [];

  for (const field of INTAKE_FIELDS) {
    const value = formatValue(intake[field.key]);
    if (!value) continue;
    lines.push(`${field.label}: ${value}`);
  }

  return lines.join("\n");
}

function buildSubmissionPayloadNotes({ intake, notes }) {
  const intakeText = buildStructuredIntakeText(intake);
  const freeText = String(notes || "").trim();

  if (intakeText && freeText) {
    return `Structured Assessment Intake:
${intakeText}

Documentation:
${freeText}`;
  }

  if (intakeText) {
    return `Structured Assessment Intake:
${intakeText}`;
  }

  return freeText;
}

// === Guided Documentation Questions ===
// Turns a set of follow-up questions (already produced by the existing
// Review/QA engines) plus the clinician's answers into a labeled text block
// that can be appended to the visit notes. Mirrors buildStructuredIntakeText's
// "skip anything left blank" convention.
function buildGuidedAnswersText(questions, answers) {
  const lines = [];

  for (const question of Array.isArray(questions) ? questions : []) {
    const answer = formatValue(answers?.[question]);
    if (!answer) continue;
    lines.push(`Q: ${question}`);
    lines.push(`A: ${answer}`);
  }

  return lines.join("\n");
}

function buildReadableExport(result) {
  const sections = [];
  const qaCounts = getQaSummaryCounts(result?.qaSummary || {});

  sections.push("OasisCopilot Review");

  if (result?.mode) sections.push(`Mode: ${result.mode}`);

  if (result?.submissionDecision) {
    sections.push("");
    sections.push(`Status: ${result.submissionDecision.status || ""}`);
    sections.push(`Reason: ${result.submissionDecision.reason || ""}`);

    if (Array.isArray(result.submissionDecision.blockingItems) && result.submissionDecision.blockingItems.length) {
      sections.push(`Blocking items: ${result.submissionDecision.blockingItems.join(", ")}`);
    }

    if (Array.isArray(result.submissionDecision.warningItems) && result.submissionDecision.warningItems.length) {
      sections.push(`Review items: ${result.submissionDecision.warningItems.join(", ")}`);
    }
  }

  if (result?.qaSummary) {
    sections.push("");
    sections.push("QA Summary");
    sections.push(`Reviewed: ${qaCounts.totalItems}`);
    sections.push(`Supported: ${qaCounts.supported}`);
    sections.push(`Partially Supported: ${qaCounts.partiallySupported}`);
    sections.push(`Unsupported: ${qaCounts.unsupported}`);
    sections.push(`Conflicting: ${qaCounts.conflicting}`);
  }

  if (Array.isArray(result?.caughtIssues) && result.caughtIssues.length) {
    sections.push("");
    sections.push("What was caught");
    result.caughtIssues.forEach((item) => sections.push(`- ${item}`));
  }

  if (result?.intake) {
    sections.push("");
    sections.push(`Intake status: ${result.intake.status || ""}`);
    if (Array.isArray(result.intake.missingDomains) && result.intake.missingDomains.length) {
      sections.push(`Documentation items to review: ${result.intake.missingDomains.join(", ")}`);
    }
    if (Array.isArray(result.intake.prompts) && result.intake.prompts.length) {
      sections.push("What to document next:");
      result.intake.prompts.forEach((prompt) => sections.push(`- ${prompt}`));
    }
  }

  if (Array.isArray(result?.topBlockingIssues) && result.topBlockingIssues.length) {
    sections.push("");
    sections.push("Top issues");
    result.topBlockingIssues.forEach((issue, idx) => {
      sections.push(`${idx + 1}. ${issue.item || ""}`);
      sections.push(`   Problem: ${issue.problem || ""}`);
      if (issue.impact) sections.push(`   Impact: ${issue.impact}`);
      if (issue.whyItMatters) sections.push(`   Why it matters: ${issue.whyItMatters}`);
      if (issue.evidence) sections.push(`   Evidence: ${issue.evidence}`);
      if (issue.supportStatus) sections.push(`   Support status: ${issue.supportStatus}`);
      if (issue?.tentativeResponse?.response) {
        sections.push(`   Most likely OASIS response (pending clarification): ${issue.tentativeResponse.response}`);
      }
      if (Array.isArray(issue.required) && issue.required.length) {
        sections.push(`   Required: ${issue.required.join(", ")}`);
      }
      if (issue.copyText) {
        sections.push(`   Copy-ready wording: ${issue.copyText}`);
      }
    });
  }

  if (result?.details?.suggestedResponses?.length > 0) {
    sections.push("");
    sections.push("Coding support");
    result.details.suggestedResponses.forEach((item) => {
      sections.push(`- ${item.item}: ${item.suggestedResponse}`);
      if (item.supportLevel) sections.push(`  Support level: ${normalizeSupportLevel(item.supportLevel)}`);
      if (Array.isArray(item.supportingQuotes) && item.supportingQuotes.length) {
        sections.push(`  Evidence: ${item.supportingQuotes.join(" | ")}`);
      }
    });
  }

  if (result?.nextStep) {
    sections.push("");
    sections.push(`Next step: ${result.nextStep}`);
  }

  return sections.join("\n");
}

function FooterLinks({ showAuthedLinks, signOut, accentBlue, muted }) {
  return (
    <div style={{ marginTop: 18, fontSize: 12, color: muted, lineHeight: 1.8 }}>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <span>
          Support:{" "}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            style={{ color: accentBlue, textDecoration: "none", fontWeight: 700 }}
          >
            {SUPPORT_EMAIL}
          </a>
        </span>

        <span>•</span>

        <a
          href={DEMO_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: accentBlue, textDecoration: "none", fontWeight: 700 }}
        >
          Demo
        </a>

        <span>•</span>

        <a
          href={PRIVACY_PATH}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: accentBlue, textDecoration: "none", fontWeight: 700 }}
        >
          Privacy
        </a>

        {showAuthedLinks && (
          <>
            <span>•</span>
            <button
              onClick={signOut}
              style={{
                border: "none",
                background: "transparent",
                padding: 0,
                cursor: "pointer",
                color: accentBlue,
                fontWeight: 700
              }}
            >
              Sign out
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function PlanButton({ href, children, primary, accentBlue }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "14px 16px",
        minHeight: 48,
        borderRadius: 12,
        textDecoration: "none",
        fontWeight: 800,
        fontSize: 14,
        width: "100%",
        maxWidth: 280,
        border: primary ? "none" : "1px solid #d1d5db",
        background: primary ? accentBlue : "#ffffff",
        color: primary ? "#ffffff" : "#111827"
      }}
    >
      {children}
    </a>
  );
}

function PricingCard({
  title,
  price,
  period,
  description,
  bullets,
  href,
  buttonText,
  accentBlue,
  border,
  muted,
  highlighted,
  badge,
  valueLine
}) {
  return (
    <div
      style={{
        background: "#fff",
        border: highlighted ? `2px solid ${accentBlue}` : `1px solid ${border}`,
        borderRadius: 10,
        padding: 18,
        position: "relative",
        flex: "1 1 0",
        boxShadow: highlighted ? "0 12px 28px rgba(47,93,138,0.08)" : "none"
      }}
    >
      {badge && (
        <div
          style={{
            position: "absolute",
            top: -10,
            right: 14,
            background: accentBlue,
            color: "#fff",
            padding: "6px 10px",
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 800
          }}
        >
          {badge}
        </div>
      )}

      <div style={{ fontWeight: 900, fontSize: 20, marginBottom: 6, color: "#111827" }}>{title}</div>

      <div style={{ marginBottom: 10, display: "flex", alignItems: "baseline", gap: 4 }}>
        <span style={{ fontSize: 38, fontWeight: 900, color: accentBlue, lineHeight: 1 }}>{price}</span>
        <span style={{ color: muted, marginLeft: 2, fontSize: 14 }}>{period}</span>
      </div>

      <div style={{ fontSize: 14, color: "#374151", lineHeight: 1.65, marginBottom: 14 }}>{description}</div>

      {valueLine && (
        <div
          style={{
            marginBottom: 12,
            fontSize: 13,
            lineHeight: 1.6,
            color: accentBlue,
            fontWeight: 700
          }}
        >
          {valueLine}
        </div>
      )}

      <div
        style={{
          marginBottom: 16,
          padding: 12,
          border: `1px solid ${border}`,
          borderRadius: 12,
          background: "#f9fafb",
          fontSize: 13,
          lineHeight: 1.7,
          color: "#111827"
        }}
      >
        {bullets.map((bullet, idx) => (
          <div key={idx}>• {bullet}</div>
        ))}
      </div>

      <PlanButton href={href} primary={highlighted} accentBlue={accentBlue}>
        {buttonText}
      </PlanButton>
    </div>
  );
}

function FAQItem({ question, answer, text, border }) {
  return (
    <div style={{ paddingBottom: 14, marginBottom: 14, borderBottom: `1px solid ${border}` }}>
      <div style={{ fontWeight: 800, fontSize: 14, color: text, marginBottom: 6 }}>{question}</div>
      <div style={{ fontSize: 13.5, color: "#374151", lineHeight: 1.65 }}>{answer}</div>
    </div>
  );
}

// === Landing page redesign components ===
// All new. Nothing existing (PricingCard, WorkflowCard, FAQItem, PlanButton,
// FooterLinks, or any state/handler in App()) is modified. These consume the
// same color constants (accentBlue, text, muted, border) already defined in
// App(), rather than introducing a second palette.

function EnterpriseNav() {
  const linkStyle = { color: ENT_TEXT, textDecoration: "none", fontSize: 14, fontWeight: 600 };
  return (
    <div style={{ borderBottom: `1px solid ${ENT_BORDER}`, background: "#ffffff", position: "sticky", top: 0, zIndex: 20 }}>
      <div
        style={{
          maxWidth: 1160,
          margin: "0 auto",
          padding: "14px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 14
        }}
      >
        <img src={LOGO_LOCKUP_DATA_URI} alt="OasisCopilot" style={{ height: 30, width: "auto", display: "block" }} />

        <div className="navLinks" style={{ display: "flex", alignItems: "center", gap: 28, flexWrap: "wrap" }}>
          <a href="#capabilities" style={linkStyle}>Product</a>
          <a href="#workflow" style={linkStyle}>How It Works</a>
          <a href="#pricing" style={linkStyle}>Pricing</a>
          <a href="#trust" style={linkStyle}>Trust</a>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <a
            href="#sign-in"
            style={{
              color: ENT_BLUE,
              textDecoration: "none",
              fontSize: 14,
              fontWeight: 700,
              padding: "9px 14px",
              borderRadius: 8,
              border: `1px solid ${ENT_BORDER}`
            }}
          >
            Login
          </a>
          <StartReviewCTA style={{ padding: "11px 18px", minHeight: 0, fontSize: 14 }}>Start a Review</StartReviewCTA>
        </div>
      </div>
    </div>
  );
}

function StartReviewCTA({ children, style }) {
  return (
    <a
      href={STRIPE_INDIVIDUAL_URL}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "15px 26px",
        minHeight: 50,
        borderRadius: 10,
        background: ENT_BLUE,
        color: "#ffffff",
        fontWeight: 800,
        fontSize: 15,
        textDecoration: "none",
        boxShadow: "0 10px 24px rgba(23,105,170,0.28)",
        ...style
      }}
    >
      {children}
    </a>
  );
}

function ProductDemoPanel() {
  const stepBadgeStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 24,
    height: 24,
    borderRadius: 7,
    background: ENT_NAVY,
    color: "#ffffff",
    fontSize: 11,
    fontWeight: 700
  };
  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: 14,
        overflow: "hidden",
        border: `1px solid ${ENT_BORDER}`,
        boxShadow: "0 40px 90px rgba(4,16,28,0.35)"
      }}
    >
      {/* App chrome bar, for an authentic workspace feel */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "13px 20px",
          background: ENT_SURFACE,
          borderBottom: `1px solid ${ENT_BORDER}`
        }}
      >
        <div style={{ display: "flex", gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: 999, background: "#C7D2DA", display: "inline-block" }} />
          <span style={{ width: 8, height: 8, borderRadius: 999, background: "#C7D2DA", display: "inline-block" }} />
          <span style={{ width: 8, height: 8, borderRadius: 999, background: "#C7D2DA", display: "inline-block" }} />
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: ENT_MUTED, letterSpacing: "0.01em" }}>OasisCopilot Workspace</div>
        <div
          style={{
            marginLeft: "auto",
            fontSize: 10,
            fontWeight: 700,
            color: "#9AAAB8",
            textTransform: "uppercase",
            letterSpacing: "0.06em"
          }}
        >
          Example workflow
        </div>
      </div>

      <div style={{ padding: "26px 22px 26px" }}>
        {/* Step 1: Review and find */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <span style={stepBadgeStyle}>1</span>
          <div style={{ fontWeight: 700, fontSize: 13.5, color: ENT_TEXT }}>Review finds 3 items need attention</div>
        </div>

        {[
          ["Medication Management", "Needs clarification", true],
          ["Transfer Ability", "Documentation incomplete", false],
          ["Bathing", "Assistance level unclear", false]
        ].map(([item, note, active]) => (
          <div
            key={item}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "11px 13px",
              marginBottom: 6,
              borderRadius: 8,
              background: active ? ENT_SURFACE : "transparent",
              border: active ? `1px solid ${ENT_BORDER}` : "1px solid transparent",
              fontSize: 13
            }}
          >
            <span style={{ fontWeight: 600, color: ENT_TEXT }}>{item}</span>
            <span style={{ color: "#8A5A16", fontWeight: 700, fontSize: 11.5 }}>{note}</span>
          </div>
        ))}

        <div style={{ height: 1, background: ENT_BORDER, margin: "20px 0" }} />

        {/* Step 2: Resolve, expanding the selected item */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <span style={stepBadgeStyle}>2</span>
          <div style={{ fontWeight: 700, fontSize: 13.5, color: ENT_TEXT }}>Resolve Medication Management</div>
        </div>

        <div
          style={{
            background: ENT_BG,
            border: `1px solid ${ENT_BORDER}`,
            borderLeft: `3px solid ${ENT_TEAL}`,
            borderRadius: 8,
            padding: "14px 16px",
            fontSize: 12.5,
            lineHeight: 1.7,
            color: ENT_MUTED,
            marginBottom: 14
          }}
        >
          <div style={{ marginBottom: 7 }}>
            <b style={{ color: ENT_TEXT }}>Why flagged: </b>setup support is documented, but the exact
            assistance level is unclear.
          </div>
          <div style={{ marginBottom: 7 }}>
            <b style={{ color: ENT_TEXT }}>Evidence: </b>daughter helps with medications.
          </div>
          <div>
            <b style={{ color: ENT_TEXT }}>Focused question: </b>does the patient need reminders, setup,
            or full administration.
          </div>
        </div>

        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 11.5,
            fontWeight: 700,
            color: "#1D6E4C"
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: 999, background: "#1D6E4C", display: "inline-block" }} />
          Resolved and rechecked
        </div>

        <div style={{ height: 1, background: ENT_BORDER, margin: "20px 0" }} />

        {/* Step 3: Verify, final status */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <span style={stepBadgeStyle}>3</span>
          <div style={{ fontWeight: 700, fontSize: 13.5, color: ENT_TEXT }}>Final visit QA status</div>
        </div>

        <div
          style={{
            padding: "14px 16px",
            borderRadius: 8,
            background: ENT_NAVY,
            border: `1px solid ${ENT_NAVY}`
          }}
        >
          <div style={{ fontSize: 10, fontWeight: 700, color: "#7EE0D8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
            Status
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#ffffff" }}>
            READY FOR FINAL CLINICIAN REVIEW
          </div>
        </div>
      </div>
    </div>
  );
}

function LandingHero({ onSecondaryClick }) {
  return (
    <div
      style={{
        background: `linear-gradient(160deg, ${ENT_NAVY_DEEP} 0%, ${ENT_NAVY} 62%, #123049 100%)`,
        borderRadius: 16,
        padding: "64px 40px",
        marginBottom: 0,
        position: "relative",
        overflow: "hidden"
      }}
    >
      <div className="heroGrid" style={{ display: "flex", gap: 56, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 460px", position: "relative" }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "6px 14px",
              borderRadius: 4,
              background: "rgba(14,159,154,0.12)",
              border: `1px solid rgba(14,159,154,0.4)`,
              color: "#7EE0D8",
              fontSize: 11.5,
              fontWeight: 700,
              letterSpacing: "0.07em",
              textTransform: "uppercase",
              marginBottom: 28
            }}
          >
            Home Health Documentation Platform
          </div>

          <h1
            className="heroTitle"
            style={{
              margin: "0 0 20px",
              color: "#ffffff",
              fontWeight: 700,
              fontSize: 48,
              lineHeight: 1.14,
              letterSpacing: "-0.015em"
            }}
          >
            Finish home health documentation faster and with confidence.
          </h1>

          <div
            className="heroSub"
            style={{
              fontSize: 18,
              color: "#C3D3E3",
              fontWeight: 400,
              lineHeight: 1.65,
              marginBottom: 30,
              maxWidth: 520
            }}
          >
            OasisCopilot helps clinicians and QA teams identify documentation issues, clarify
            missing information, resolve problems, generate copy-ready content, recheck the
            assessment, and complete a final visit QA review, all in one streamlined workflow.
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 5,
              marginBottom: 32,
              paddingLeft: 16,
              borderLeft: "2px solid rgba(126,224,216,0.5)",
              maxWidth: 480
            }}
          >
            <div style={{ fontSize: 14.5, color: "#ffffff", fontWeight: 600 }}>
              No training required. No demo required.
            </div>
            <div style={{ fontSize: 14, color: "#9DB2C6", fontWeight: 400 }}>
              Just paste your visit notes and get started.
            </div>
          </div>

          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            <StartReviewCTA>Start a Review</StartReviewCTA>
            <a
              href={DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onSecondaryClick}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "15px 24px",
                minHeight: 50,
                borderRadius: 8,
                background: "transparent",
                color: "#ffffff",
                fontWeight: 600,
                fontSize: 15,
                textDecoration: "none",
                border: "1px solid rgba(255,255,255,0.25)"
              }}
            >
              See how it works
            </a>
          </div>
        </div>

        <div style={{ flex: "1 1 400px", maxWidth: 460, margin: "0 auto" }}>
          <ProductDemoPanel />
        </div>
      </div>
    </div>
  );
}

function TrustStrip() {
  return (
    <div style={{ marginBottom: 0, display: "flex", justifyContent: "center" }}>
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 18px",
          borderRadius: 6,
          background: "#EAF3FA",
          border: `1px solid ${ENT_BORDER}`,
          color: ENT_NAVY,
          fontSize: 13.5,
          fontWeight: 600
        }}
      >
        Purpose-built for OASIS-E2 home health documentation
      </div>
    </div>
  );
}

function SimplicityBanner() {
  const items = [
    ["Simple by design", "Sophisticated technology, an extremely simple experience."],
    ["No training required", "There is nothing to learn before you start."],
    ["No demo required", "Just paste your visit notes and get started."]
  ];
  return (
    <div className="simplicityRow" style={{ display: "flex", flexWrap: "wrap", padding: "32px 4px 0" }}>
      {items.map(([title, body], idx) => (
        <div
          key={title}
          style={{
            flex: "1 1 220px",
            padding: idx === 0 ? "0 24px 0 0" : "0 24px",
            borderLeft: idx === 0 ? "none" : `1px solid ${ENT_BORDER}`
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 700, color: ENT_NAVY, marginBottom: 6 }}>{title}</div>
          <div style={{ fontSize: 13.5, color: ENT_MUTED, lineHeight: 1.6 }}>{body}</div>
        </div>
      ))}
    </div>
  );
}

function AudienceSection() {
  const audiences = [
    ["Field Clinicians", "Catch what needs attention while the visit details are still fresh."],
    ["QA Reviewers", "Focus your attention where documentation needs another look."],
    ["Home Health Agencies", "Give your team a consistent documentation workflow."]
  ];
  return (
    <div style={{ padding: "72px 4px" }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: ENT_TEAL, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 14 }}>
        Built for your whole workflow
      </div>
      <h2 style={{ margin: "0 0 44px", fontSize: 32, fontWeight: 700, color: ENT_TEXT, maxWidth: 640, lineHeight: 1.28 }}>
        OasisCopilot serves individual clinicians, QA reviewers, and agency teams.
      </h2>
      <div className="audienceRow" style={{ display: "flex", flexWrap: "wrap", borderTop: `1px solid ${ENT_BORDER}` }}>
        {audiences.map(([title, body], idx) => (
          <div
            key={title}
            style={{
              flex: "1 1 260px",
              padding: "26px 24px 0 0",
              borderRight: idx < audiences.length - 1 ? `1px solid ${ENT_BORDER}` : "none",
              marginRight: idx < audiences.length - 1 ? 24 : 0
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 700, color: ENT_TEXT, marginBottom: 10 }}>{title}</div>
            <div style={{ fontSize: 14.5, color: ENT_MUTED, lineHeight: 1.65 }}>{body}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WorkflowSequenceSection() {
  const steps = [
    ["Review", "See what needs attention as you document."],
    ["Resolve", "Work through unclear items one at a time."],
    ["Complete", "Turn clarified answers into usable content."],
    ["Recheck", "Confirm the update actually resolved it."],
    ["Final Visit QA", "Know exactly where the chart stands."]
  ];
  return (
    <div style={{ padding: "72px 4px" }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: ENT_TEAL, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 14 }}>
        More than a review tool
      </div>
      <h2 style={{ margin: "0 0 18px", fontSize: 32, fontWeight: 700, color: ENT_TEXT, maxWidth: 680, lineHeight: 1.28 }}>
        A complete path from visit documentation to a QA ready assessment.
      </h2>
      <div style={{ fontSize: 16, color: ENT_MUTED, lineHeight: 1.7, maxWidth: 620, marginBottom: 52 }}>
        OasisCopilot helps you move all the way from documenting the visit to a final, reviewed,
        resolved, and QA ready assessment.
      </div>

      <div className="workflowRow" style={{ display: "flex", flexWrap: "wrap", gap: 0, borderTop: `1px solid ${ENT_BORDER}` }}>
        {steps.map(([title, desc], idx) => (
          <div
            key={title}
            style={{
              flex: "1 1 180px",
              padding: "24px 20px 0",
              borderRight: idx < steps.length - 1 ? `1px solid ${ENT_BORDER}` : "none"
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: ENT_TEAL, marginBottom: 12 }}>
              {String(idx + 1).padStart(2, "0")}
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: ENT_TEXT, marginBottom: 8 }}>{title}</div>
            <div style={{ fontSize: 13.5, color: ENT_MUTED, lineHeight: 1.6 }}>{desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CapabilitiesSection() {
  const capabilities = [
    ["Live Review", "See what needs attention while you are still documenting the visit."],
    ["Smart Resolution", "Get focused guidance when documentation needs clarification."],
    ["Guided Questions", "Work through what is unclear with one focused question at a time."],
    ["Insert Fix", "Turn a clarified answer into documentation you can review and use."],
    ["Complete Documentation", "Build a structured draft from the information you already have."],
    ["Narrative Generator", "Create a clear narrative draft without starting from a blank page."]
  ];
  return (
    <div style={{ padding: "72px 4px" }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: ENT_TEAL, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 14 }}>
        Core capabilities
      </div>
      <h2 style={{ margin: "0 0 40px", fontSize: 32, fontWeight: 700, color: ENT_TEXT, maxWidth: 640, lineHeight: 1.28 }}>
        Everything you need between documentation and submission.
      </h2>

      <div className="capabilitiesGrid" style={{ display: "flex", flexWrap: "wrap", gap: "0 48px", marginBottom: 44 }}>
        {capabilities.map(([title, body]) => (
          <div
            key={title}
            style={{
              flex: "1 1 340px",
              padding: "22px 0",
              borderBottom: `1px solid ${ENT_BORDER}`
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, color: ENT_TEXT, marginBottom: 6 }}>{title}</div>
            <div style={{ fontSize: 14, color: ENT_MUTED, lineHeight: 1.6 }}>{body}</div>
          </div>
        ))}
      </div>

      <div
        style={{
          padding: "34px 36px",
          borderRadius: 12,
          background: ENT_NAVY,
          display: "flex",
          alignItems: "center",
          gap: 28,
          flexWrap: "wrap"
        }}
      >
        <div style={{ flex: "2 1 300px" }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: "#7EE0D8", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 10 }}>
            The final checkpoint
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#ffffff", marginBottom: 8 }}>Final Visit QA</div>
          <div style={{ fontSize: 14.5, color: "#B9C9D8", lineHeight: 1.65, maxWidth: 480 }}>
            Know exactly what still needs attention before you submit, with one clear status for
            every chart.
          </div>
        </div>
        <div style={{ flex: "1 1 200px", display: "flex", justifyContent: "flex-end" }}>
          <div
            style={{
              padding: "12px 18px",
              borderRadius: 8,
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.16)",
              color: "#ffffff",
              fontSize: 13.5,
              fontWeight: 700,
              whiteSpace: "nowrap"
            }}
          >
            READY FOR FINAL CLINICIAN REVIEW
          </div>
        </div>
      </div>
    </div>
  );
}

function ProductShowcaseSection() {
  return (
    <div style={{ padding: "72px 4px" }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: ENT_TEAL, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 14 }}>
        Final Visit QA
      </div>
      <h2 style={{ margin: "0 0 16px", fontSize: 28, fontWeight: 700, color: ENT_TEXT, maxWidth: 620, lineHeight: 1.28 }}>
        Know exactly where a chart stands before you submit it.
      </h2>
      <div style={{ fontSize: 15, color: ENT_MUTED, lineHeight: 1.7, maxWidth: 600, marginBottom: 8 }}>
        Every visit reaches one clear status, so you always know what still needs attention.
      </div>
      <div
        style={{
          marginTop: 24,
          padding: "26px 28px",
          borderRadius: 12,
          background: "#ffffff",
          border: `1px solid ${ENT_BORDER}`
        }}
      >
        <FinalQAStatusRow />
      </div>
    </div>
  );
}

function TrustSection() {
  const points = [
    ["Built for OASIS-E2", "Aligned to the current OASIS-E2 item set used in home health documentation."],
    ["You stay in control", "Every suggestion is reviewed by you before it becomes part of the record."],
    ["Structured, transparent review", "Every flagged item shows why it was flagged, so nothing is a black box."]
  ];
  return (
    <div style={{ padding: "72px 4px", background: ENT_NAVY, borderRadius: 12 }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: "#7EE0D8", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 14 }}>
        Trust
      </div>
      <h2 style={{ margin: "0 0 40px", fontSize: 28, fontWeight: 700, color: "#ffffff", maxWidth: 620, lineHeight: 1.28 }}>
        Built to be trusted with real documentation work.
      </h2>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0 40px" }}>
        {points.map(([title, body], idx) => (
          <div
            key={title}
            style={{
              flex: "1 1 260px",
              padding: "0 0 4px",
              borderTop: "1px solid rgba(255,255,255,0.14)",
              paddingTop: 22,
              marginTop: idx === 0 ? 0 : 0
            }}
          >
            <div style={{ fontSize: 15.5, fontWeight: 700, color: "#ffffff", marginBottom: 10 }}>{title}</div>
            <div style={{ fontSize: 13.5, color: "#B0C2D2", lineHeight: 1.65 }}>{body}</div>
          </div>
        ))}
      </div>
    </div>
  );
}


function FinalQAStatusRow() {
  const states = [
    ["DO NOT SUBMIT", "#B42318", "Blocking clinical or documentation issues remain."],
    ["REVIEW REQUIRED", "#8A5A16", "Non-blocking items still need clinician attention."],
    ["READY FOR FINAL CLINICIAN REVIEW", "#1D6E4C", "No outstanding issues detected."]
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0, marginTop: 4 }}>
      {states.map(([label, color, desc], idx) => (
        <div
          key={label}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            padding: "16px 4px",
            borderTop: idx === 0 ? "none" : `1px solid ${ENT_BORDER}`
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: 999, background: color, flexShrink: 0 }} />
          <div style={{ minWidth: 260 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: ENT_TEXT, letterSpacing: "0.01em" }}>{label}</span>
          </div>
          <div style={{ fontSize: 13, color: ENT_MUTED }}>{desc}</div>
        </div>
      ))}
    </div>
  );
}


function SectionList({ items, emptyText = "(None.)" }) {
  if (!Array.isArray(items) || !items.length) return <>{emptyText}</>;
  return (
    <>
      {items.map((item, idx) => (
        <div key={idx} style={{ marginBottom: 10 }}>
          • {String(item)}
        </div>
      ))}
    </>
  );
}

function ResultCard({ title, titleColor, borderColor, bgColor, children }) {
  return (
    <div
      style={{
        padding: 16,
        border: `1px solid ${borderColor || "#e5e7eb"}`,
        background: bgColor || "#fff",
        borderRadius: 14,
        marginBottom: 14
      }}
    >
      <h3
        style={{
          marginTop: 0,
          marginBottom: 10,
          color: titleColor || "#111827",
          fontSize: 16,
          fontWeight: 800
        }}
      >
        {title}
      </h3>
      <div
        style={{
          whiteSpace: "pre-wrap",
          margin: 0,
          fontFamily: "inherit",
          fontSize: 14,
          lineHeight: 1.65
        }}
      >
        {children}
      </div>
    </div>
  );
}

// === Guided Documentation Questions panel ===
// Presents the review's existing follow-up questions one at a time instead
// of only as a static list. Reuses ResultCard for visual consistency with
// every other result section.
function GuidedQuestionsPanel({
  questions,
  active,
  snapshot,
  stepIndex,
  draft,
  busy,
  onStart,
  onDraftChange,
  onBack,
  onContinue,
  onCancel,
  accentBlue,
  text,
  muted,
  border
}) {
  if (!Array.isArray(questions) || questions.length === 0) return null;

  const primaryButtonStyle = {
    padding: "13px 14px",
    borderRadius: 12,
    cursor: busy ? "not-allowed" : "pointer",
    background: accentBlue,
    color: "#fff",
    border: "none",
    fontWeight: 800
  };

  const secondaryButtonStyle = {
    padding: "13px 14px",
    borderRadius: 12,
    cursor: "pointer",
    background: "#fff",
    color: text,
    border: `1px solid ${border}`,
    fontWeight: 800
  };

  const plainButtonStyle = {
    padding: "13px 14px",
    borderRadius: 12,
    cursor: "pointer",
    background: "transparent",
    color: muted,
    border: "none",
    fontWeight: 700
  };

  if (!active) {
    return (
      <ResultCard title="Guided documentation questions" titleColor={text} borderColor={border}>
        <div style={{ fontSize: 14, color: "#374151", marginBottom: 12 }}>
          Answer {questions.length} follow-up question{questions.length === 1 ? "" : "s"} one at a time.
          Submitting will add your answers to the documentation and re-run the review.
        </div>
        <button onClick={onStart} style={primaryButtonStyle}>
          Answer questions
        </button>
      </ResultCard>
    );
  }

  const currentQuestion = snapshot[stepIndex] || "";
  const isLast = stepIndex === snapshot.length - 1;

  return (
    <ResultCard title="Guided documentation questions" titleColor={text} borderColor={border}>
      <div style={{ fontSize: 12, fontWeight: 700, color: muted, marginBottom: 10 }}>
        Question {stepIndex + 1} of {snapshot.length}
      </div>

      <div style={{ fontSize: 15, fontWeight: 800, color: text, marginBottom: 10 }}>
        {currentQuestion}
      </div>

      <textarea
        rows={3}
        value={draft}
        onChange={(e) => onDraftChange(e.target.value)}
        placeholder="Type the answer here…"
        style={{
          width: "100%",
          padding: 10,
          border: `1px solid ${border}`,
          borderRadius: 12,
          fontSize: 14,
          lineHeight: 1.55,
          resize: "vertical",
          boxSizing: "border-box"
        }}
      />

      <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
        <button onClick={onBack} disabled={stepIndex === 0 || busy} style={secondaryButtonStyle}>
          Back
        </button>

        <button onClick={onContinue} disabled={busy} style={primaryButtonStyle}>
          {busy ? "Working…" : isLast ? "Submit & re-run review" : "Continue"}
        </button>

        <button onClick={onCancel} disabled={busy} style={plainButtonStyle}>
          Cancel
        </button>
      </div>
    </ResultCard>
  );
}

// Sections shown by DocumentationPackagePanel, in display order. Keys match
// the fields returned by POST /api/generate-documentation exactly.
const DOCUMENTATION_PACKAGE_SECTIONS_UI = [
  { key: "skilledNursingNarrative", label: "Skilled Nursing Narrative" },
  { key: "medicationDocumentation", label: "Medication Documentation" },
  { key: "homeboundStatement", label: "Homebound Statement" },
  { key: "adlDocumentation", label: "ADL Documentation" },
  { key: "transferDocumentation", label: "Transfer Documentation" },
  { key: "ambulationDocumentation", label: "Ambulation Documentation" },
  { key: "bathingDocumentation", label: "Bathing Documentation" },
  { key: "dressingDocumentation", label: "Dressing Documentation" },
  { key: "teachingDocumentation", label: "Teaching Documentation" },
  { key: "interventions", label: "Interventions" },
  { key: "oasisSupportingStatements", label: "OASIS Supporting Statements" }
];

// Small "Paste this into your note" block with its own Copy button. Reuses
// the exact visual pattern established in IssueCard (Feature 3's redesign)
// rather than inventing a new copy-block style.
function PasteBlock({ text, onCopyText, onInsert, accentBlue, border }) {
  if (!text) return null;
  return (
    <div
      style={{
        padding: 12,
        borderRadius: 12,
        background: "#ffffff",
        border: `1px solid ${border}`
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 10,
          marginBottom: 6
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 800, color: "#374151" }}>Paste this into your note</div>
        <div style={{ display: "flex", gap: 6 }}>
          {onInsert && (
            <button
              onClick={() => onInsert(text)}
              style={{
                border: `1px solid ${border}`,
                background: "#fff",
                color: accentBlue,
                borderRadius: 8,
                padding: "5px 10px",
                cursor: "pointer",
                fontWeight: 800,
                fontSize: 11
              }}
            >
              Insert Fix
            </button>
          )}
          <button
            onClick={() => onCopyText?.(text)}
            style={{
              border: `1px solid ${border}`,
              background: "#fff",
              color: accentBlue,
              borderRadius: 8,
              padding: "5px 10px",
              cursor: "pointer",
              fontWeight: 800,
              fontSize: 11
            }}
          >
            Copy
          </button>
        </div>
      </div>
      <div style={{ fontSize: 14, lineHeight: 1.65, color: "#111827", whiteSpace: "pre-wrap" }}>{text}</div>
    </div>
  );
}

function ConfidenceBadge({ confidence, muted, border }) {
  if (!confidence) return null;
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "4px 10px",
        borderRadius: 999,
        background: "#fff",
        border: `1px solid ${border}`,
        color: muted,
        fontSize: 11,
        fontWeight: 800,
        textTransform: "capitalize",
        marginBottom: 12
      }}
    >
      Confidence: {confidence}
    </div>
  );
}

function DocumentationPackagePanel({ result, onCopyText, accentBlue, text, muted, border }) {
  if (!result) return null;

  const sectionsWithContent = DOCUMENTATION_PACKAGE_SECTIONS_UI.filter((s) => result[s.key]);

  return (
    <div style={{ marginTop: 22 }}>
      <div style={{ fontWeight: 900, fontSize: 18, color: text, marginBottom: 10 }}>
        Complete Documentation Package
      </div>

      <ConfidenceBadge confidence={result.confidence} muted={muted} border={border} />

      {Array.isArray(result.missingInformation) && result.missingInformation.length > 0 && (
        <ResultCard title="Missing Information" titleColor={text} borderColor={border}>
          <SectionList items={result.missingInformation} />
        </ResultCard>
      )}

      {sectionsWithContent.length === 0 ? (
        <ResultCard title="No sections could be generated" titleColor={text} borderColor={border}>
          The visit notes did not contain enough information to generate any documentation sections. See Missing
          Information above for what to add.
        </ResultCard>
      ) : (
        sectionsWithContent.map((section) => (
          <ResultCard key={section.key} title={section.label} titleColor={text} borderColor={border}>
            <PasteBlock text={result[section.key]} onCopyText={onCopyText} accentBlue={accentBlue} border={border} />
          </ResultCard>
        ))
      )}
    </div>
  );
}

function NarrativePanel({ result, onCopyText, accentBlue, text, muted, border }) {
  if (!result) return null;

  return (
    <div style={{ marginTop: 22 }}>
      <div style={{ fontWeight: 900, fontSize: 18, color: text, marginBottom: 10 }}>Generated Narrative</div>

      <ConfidenceBadge confidence={result.confidence} muted={muted} border={border} />

      {Array.isArray(result.missingInformation) && result.missingInformation.length > 0 && (
        <ResultCard title="Missing Information" titleColor={text} borderColor={border}>
          <SectionList items={result.missingInformation} />
        </ResultCard>
      )}

      {result.narrative ? (
        <ResultCard title="Narrative" titleColor={text} borderColor={border}>
          <PasteBlock text={result.narrative} onCopyText={onCopyText} accentBlue={accentBlue} border={border} />
        </ResultCard>
      ) : (
        <ResultCard title="Narrative could not be generated" titleColor={text} borderColor={border}>
          The visit notes did not contain enough information to generate a narrative. See Missing Information above.
        </ResultCard>
      )}
    </div>
  );
}

function IssueCard({ issue, idx, isExpanded, onToggle, onCopyText, onInsertFix, accentBlue, border }) {
  const blocking = issue?.severity === "blocking";
  const palette = blocking
    ? {
        bg: "#fef2f2",
        border: "#fecaca",
        chipText: "#991b1b",
        chipBg: "#fff",
        title: "#7f1d1d"
      }
    : {
        bg: "#fffbeb",
        border: "#fde68a",
        chipText: "#92400e",
        chipBg: "#fff",
        title: "#78350f"
      };

  const supportStatusLabel = issue?.supportStatus ? normalizeSupportLevel(issue.supportStatus) : "";
  const hasRequired = Array.isArray(issue?.required) && issue.required.length > 0;
  const hasSecondaryDetail = Boolean(
    issue?.impact || issue?.whyItMatters || issue?.evidence || issue?.tentativeResponse?.response
  );

  return (
    <div
      style={{
        padding: 16,
        border: `1px solid ${palette.border}`,
        background: palette.bg,
        borderRadius: 14,
        marginBottom: 14
      }}
    >
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          padding: "6px 10px",
          borderRadius: 999,
          background: palette.chipBg,
          border: `1px solid ${palette.border}`,
          color: palette.chipText,
          fontSize: 12,
          fontWeight: 900,
          marginBottom: 10
        }}
      >
        {blocking ? "BLOCKING" : "REVIEW"}
      </div>

      <div style={{ fontSize: 17, fontWeight: 900, color: palette.title, marginBottom: 6 }}>
        {idx + 1}. {issue?.item || "Issue"}
      </div>

      {supportStatusLabel && (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "4px 10px",
            borderRadius: 999,
            background: "#ffffff",
            border: `1px solid ${border}`,
            color: "#374151",
            fontSize: 11,
            fontWeight: 800,
            textTransform: "capitalize",
            marginBottom: 12
          }}
        >
          {supportStatusLabel}
        </div>
      )}

      {issue?.copyText && (
        <div style={{ marginTop: 4, marginBottom: 14 }}>
          <PasteBlock text={issue.copyText} onCopyText={onCopyText} onInsert={onInsertFix} accentBlue={accentBlue} border={border} />
        </div>
      )}

      {hasRequired && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#111827", marginBottom: 6 }}>
            Missing Information
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.7, color: "#111827" }}>
            <SectionList items={issue.required} />
          </div>
        </div>
      )}

      {issue?.problem && (
        <div style={{ marginBottom: 4 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#111827", marginBottom: 4 }}>
            Reason
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.7, color: "#374151" }}>{issue.problem}</div>
        </div>
      )}

      <button
        onClick={onToggle}
        style={{
          marginTop: 10,
          border: `1px solid ${border}`,
          background: "#fff",
          color: accentBlue,
          borderRadius: 10,
          padding: "9px 12px",
          cursor: "pointer",
          fontWeight: 800,
          fontSize: 13
        }}
      >
        {isExpanded ? "Hide details" : "Why?"}
      </button>

      {isExpanded && (
        <div
          style={{
            marginTop: 10,
            padding: 12,
            borderRadius: 12,
            background: "#ffffff",
            border: `1px solid ${border}`,
            fontSize: 13,
            lineHeight: 1.65,
            color: "#374151"
          }}
        >
          {hasSecondaryDetail ? (
            <>
              {issue?.impact && (
                <div style={{ marginBottom: 8 }}>
                  <b>Impact:</b> {issue.impact}
                </div>
              )}
              {issue?.whyItMatters && (
                <div style={{ marginBottom: 8 }}>
                  <b>Why it matters:</b> {issue.whyItMatters}
                </div>
              )}
              {issue?.evidence && (
                <div style={{ marginBottom: 8 }}>
                  <b>Evidence:</b> {issue.evidence}
                </div>
              )}
              {issue?.tentativeResponse?.response && (
                <div
                  style={{
                    marginTop: 4,
                    padding: 10,
                    borderRadius: 10,
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe"
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#1e3a8a", marginBottom: 4 }}>
                    Most likely OASIS response (pending clarification)
                  </div>
                  <div style={{ fontSize: 13, lineHeight: 1.6, color: "#111827" }}>
                    {issue.tentativeResponse.response}
                  </div>
                  {issue?.tentativeResponse?.basis && (
                    <div style={{ fontSize: 12, lineHeight: 1.5, color: "#374151", marginTop: 4 }}>
                      Basis: {issue.tentativeResponse.basis}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            issue?.explanation?.detailed || issue?.explanation?.short || issue?.problem || ""
          )}
        </div>
      )}
    </div>
  );
}

// === Smart Resolution Engine components ===
// Reuses ResultCard, PasteBlock, and the same visual language established
// by IssueCard -- no new visual system introduced.

function resolutionStatusBadge(state, muted, border) {
  if (state === RESOLUTION_STATE.RESOLVED) {
    return { label: "✓ Resolved", color: "#065f46", bg: "#ecfdf5", border: "#a7f3d0" };
  }
  if (state === RESOLUTION_STATE.STILL_NEEDS_ATTENTION) {
    return { label: "⚠ Still needs attention", color: "#92400e", bg: "#fffbeb", border: "#fde68a" };
  }
  if (state === RESOLUTION_STATE.RECHECKING || state === RESOLUTION_STATE.INSERTED) {
    return { label: "Checking…", color: muted, bg: "#f9fafb", border };
  }
  return { label: "Unresolved", color: "#374151", bg: "#f9fafb", border };
}

function ResolutionDetail({
  issue,
  state,
  draft,
  correction,
  detail,
  busy,
  onOptionChange,
  onFreeTextChange,
  onContinue,
  onCorrectionChange,
  onInsert,
  onRetry,
  onNext,
  accentBlue,
  text,
  muted,
  border
}) {
  const hasOptions = Boolean(
    issue.resolutionOptions && Array.isArray(issue.resolutionOptions.options) && issue.resolutionOptions.options.length
  );
  const questionText =
    issue.resolutionOptions?.question ||
    `What additional detail can you provide?${
      Array.isArray(issue.required) && issue.required.length ? ` (${issue.required.join(", ")})` : ""
    }`;

  const primaryButtonStyle = {
    padding: "11px 14px",
    borderRadius: 12,
    cursor: "pointer",
    background: accentBlue,
    color: "#fff",
    border: "none",
    fontWeight: 800
  };

  const secondaryButtonStyle = {
    padding: "11px 14px",
    borderRadius: 12,
    cursor: "pointer",
    background: "#fff",
    color: accentBlue,
    border: `1px solid ${border}`,
    fontWeight: 800
  };

  return (
    <ResultCard title={`Resolve: ${issue.item}`} titleColor={text} borderColor={border}>
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: "#374151", marginBottom: 4 }}>Why this was flagged</div>
        <div style={{ fontSize: 14, color: "#374151", lineHeight: 1.6 }}>
          {issue.problem || issue.whyItMatters || "This item needs clarification."}
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: "#374151", marginBottom: 4 }}>Supporting documentation</div>
        <div style={{ fontSize: 14, color: "#374151", lineHeight: 1.6 }}>
          {issue.evidence ? issue.evidence : "Insufficient supporting documentation."}
        </div>
      </div>

      {(state === RESOLUTION_STATE.AWAITING_INPUT || state === RESOLUTION_STATE.UNRESOLVED || !state) && (
        <>
          <div style={{ fontSize: 14, fontWeight: 800, color: text, marginBottom: 8 }}>{questionText}</div>

          {hasOptions ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
              {issue.resolutionOptions.options.map((opt) => (
                <label key={opt.value} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, cursor: "pointer" }}>
                  <input
                    type="radio"
                    name={`resolution-${issue.item}`}
                    checked={draft.selectedValue === opt.value}
                    onChange={() => onOptionChange(opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          ) : (
            <textarea
              rows={3}
              value={draft.freeText || ""}
              onChange={(e) => onFreeTextChange(e.target.value)}
              placeholder="Type the answer here…"
              style={{
                width: "100%",
                padding: 10,
                border: `1px solid ${border}`,
                borderRadius: 12,
                fontSize: 14,
                lineHeight: 1.55,
                resize: "vertical",
                boxSizing: "border-box",
                marginBottom: 12
              }}
            />
          )}

          <button onClick={onContinue} style={primaryButtonStyle}>
            Continue
          </button>
        </>
      )}

      {state === RESOLUTION_STATE.READY_TO_INSERT && (
        <>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#374151", marginBottom: 6 }}>Suggested Correction</div>
          <div style={{ fontSize: 12, color: muted, marginBottom: 6 }}>
            Review and edit before inserting -- this is a starting point, not a final clinical determination.
          </div>
          <textarea
            rows={4}
            value={correction}
            onChange={(e) => onCorrectionChange(e.target.value)}
            style={{
              width: "100%",
              padding: 10,
              border: `1px solid ${border}`,
              borderRadius: 12,
              fontSize: 14,
              lineHeight: 1.55,
              resize: "vertical",
              boxSizing: "border-box",
              marginBottom: 12
            }}
          />
          <button onClick={onInsert} disabled={busy || !correction.trim()} style={primaryButtonStyle}>
            Insert Fix
          </button>
        </>
      )}

      {(state === RESOLUTION_STATE.INSERTED || state === RESOLUTION_STATE.RECHECKING) && (
        <div style={{ fontSize: 14, fontWeight: 700, color: muted }}>Checking…</div>
      )}

      {state === RESOLUTION_STATE.RESOLVED && (
        <>
          <div style={{ fontSize: 15, fontWeight: 900, color: "#065f46", marginBottom: 10 }}>✓ Resolved</div>
          <button onClick={onNext} style={primaryButtonStyle}>
            Next Issue
          </button>
        </>
      )}

      {state === RESOLUTION_STATE.STILL_NEEDS_ATTENTION && (
        <>
          <div style={{ fontSize: 15, fontWeight: 900, color: "#92400e", marginBottom: 6 }}>⚠ Still Needs Attention</div>
          <div style={{ fontSize: 13, color: "#374151", marginBottom: 10 }}>
            {detail || "This issue is still present in the current documentation."}
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button onClick={onRetry} style={secondaryButtonStyle}>
              Try Again
            </button>
            <button onClick={onNext} style={primaryButtonStyle}>
              Next Issue
            </button>
          </div>
        </>
      )}
    </ResultCard>
  );
}

function SmartResolutionPanel({
  queue,
  states,
  drafts,
  corrections,
  details,
  activeItem,
  busy,
  onSelectIssue,
  onOptionChange,
  onFreeTextChange,
  onContinue,
  onCorrectionChange,
  onInsert,
  onRetry,
  onNext,
  onClose,
  accentBlue,
  text,
  muted,
  border
}) {
  if (!Array.isArray(queue) || queue.length === 0) return null;

  const resolvedCount = queue.filter((i) => states[i.item] === RESOLUTION_STATE.RESOLVED).length;
  const activeIssue = queue.find((i) => i.item === activeItem) || null;
  const activeState = activeIssue ? states[activeIssue.item] : null;

  return (
    <div style={{ marginTop: 22 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontWeight: 900, fontSize: 18, color: text }}>
          Issues to Resolve ({resolvedCount}/{queue.length} resolved)
        </div>
        <button
          onClick={onClose}
          style={{ border: "none", background: "transparent", color: muted, cursor: "pointer", fontWeight: 700, fontSize: 13 }}
        >
          Close
        </button>
      </div>

      <ResultCard title="" titleColor={text} borderColor={border}>
        {queue.map((issue, idx) => {
          const st = states[issue.item] || RESOLUTION_STATE.UNRESOLVED;
          const badge = resolutionStatusBadge(st, muted, border);
          const isActive = issue.item === activeItem;
          return (
            <div
              key={`${issue.item}-${idx}`}
              style={{
                marginBottom: idx === queue.length - 1 ? 0 : 10,
                paddingBottom: idx === queue.length - 1 ? 0 : 10,
                borderBottom: idx === queue.length - 1 ? "none" : `1px solid ${border}`
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div style={{ fontWeight: 800, color: text, fontSize: 14 }}>{issue.item}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      color: badge.color,
                      background: badge.bg,
                      border: `1px solid ${badge.border}`,
                      borderRadius: 999,
                      padding: "3px 8px"
                    }}
                  >
                    {badge.label}
                  </span>
                  <button
                    onClick={() => onSelectIssue(issue.item)}
                    disabled={busy}
                    style={{
                      border: `1px solid ${border}`,
                      background: isActive ? accentBlue : "#fff",
                      color: isActive ? "#fff" : accentBlue,
                      borderRadius: 8,
                      padding: "6px 10px",
                      cursor: busy ? "not-allowed" : "pointer",
                      fontWeight: 800,
                      fontSize: 12
                    }}
                  >
                    {st === RESOLUTION_STATE.RESOLVED ? "Review" : "Resolve"}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </ResultCard>

      {activeIssue && (
        <ResolutionDetail
          issue={activeIssue}
          state={activeState}
          draft={drafts[activeIssue.item] || {}}
          correction={corrections[activeIssue.item] || ""}
          detail={details[activeIssue.item] || ""}
          busy={busy}
          onOptionChange={(value) => onOptionChange(activeIssue.item, value)}
          onFreeTextChange={(value) => onFreeTextChange(activeIssue.item, value)}
          onContinue={() => onContinue(activeIssue)}
          onCorrectionChange={(value) => onCorrectionChange(activeIssue.item, value)}
          onInsert={() => onInsert(activeIssue)}
          onRetry={() => onRetry(activeIssue.item)}
          onNext={onNext}
          accentBlue={accentBlue}
          text={text}
          muted={muted}
          border={border}
        />
      )}
    </div>
  );
}

// === Final Visit QA Gate ===
// Reuses result.submissionDecision (the existing three-state decision --
// DO_NOT_SUBMIT/REVIEW/READY, unchanged) relabeled per the exact wording
// requested, plus counts derived from result.issues cross-referenced
// against resolutionStates. Always computed from result.issues directly
// (not the resolution queue snapshot), so it is accurate whether or not
// Smart Resolution has ever been opened -- a clean note with zero issues
// shows READY immediately with no interaction required.
function FinalVisitQAGate({ result, resolutionStates, text, muted, border }) {
  if (!result) return null;

  // Reuses the existing getSubmissionStatusStyle for colors (text/bg/
  // border) instead of a second, parallel color scheme -- only the label
  // text differs, per the exact wording required for this screen. Both the
  // label and the color are derived from the SAME normalized status value
  // so an unexpected/missing status can never show a mismatched
  // color+label combination (e.g. green colors next to a "REVIEW
  // REQUIRED" label) -- this mirrors getSubmissionStatusStyle's own
  // existing default-to-READY fallback rather than diverging from it.
  const normalizedGateStatus = String(result?.submissionDecision?.status || "").toUpperCase();
  const statusStyle = getSubmissionStatusStyle(normalizedGateStatus);
  const finalGateLabels = {
    READY: "READY FOR FINAL CLINICIAN REVIEW",
    REVIEW: "REVIEW REQUIRED",
    DO_NOT_SUBMIT: "DO NOT SUBMIT"
  };
  const statusLabel = finalGateLabels[normalizedGateStatus] || finalGateLabels.READY;

  const issues = Array.isArray(result.issues) ? result.issues : [];

  const totalIssues = issues.length;
  const resolvedIssues = issues.filter((i) => resolutionStates[i.item] === RESOLUTION_STATE.RESOLVED).length;
  const remainingIssues = totalIssues - resolvedIssues;
  const blockingRemaining = issues.filter(
    (i) => i.severity === "blocking" && resolutionStates[i.item] !== RESOLUTION_STATE.RESOLVED
  ).length;
  const reviewItemsRemaining = issues.filter(
    (i) => i.severity !== "blocking" && resolutionStates[i.item] !== RESOLUTION_STATE.RESOLVED
  ).length;

  const qa = getQaSummaryCounts(result.qaSummary || {});

  // details.intake is only populated in documentation-support mode (the
  // existing, untouched projectPrimaryResponse sets it to null for
  // pre-submit-qa mode). Fall back to the qaSummary counts -- already
  // correct in both modes -- rather than silently showing 0 in QA mode
  // while the OASIS support status line above shows real conflicting/
  // unsupported counts from the same result.
  const contradictionsRemaining = Array.isArray(result?.details?.intake?.consistencyIssues)
    ? result.details.intake.consistencyIssues.length
    : qa.conflicting;
  const gapsRemaining = result?.details?.intake
    ? (Array.isArray(result.details.intake.missingInfo) ? result.details.intake.missingInfo.length : 0) +
      (Array.isArray(result.details.intake.weakInfo) ? result.details.intake.weakInfo.length : 0)
    : qa.unsupported;

  return (
    <div style={{ marginTop: 22 }}>
      <div style={{ fontWeight: 900, fontSize: 18, color: text, marginBottom: 10 }}>Visit Documentation Check</div>

      <ResultCard title="" titleColor={text} borderColor={border}>
        <div style={{ marginBottom: 6 }}>Issues detected: {totalIssues}</div>
        <div style={{ marginBottom: 6 }}>Issues resolved: {resolvedIssues}</div>
        <div style={{ marginBottom: 6 }}>Issues remaining: {remainingIssues}</div>
        <div style={{ marginBottom: 6 }}>Blocking issues remaining: {blockingRemaining}</div>
        <div style={{ marginBottom: 6 }}>Items requiring clinician review: {reviewItemsRemaining}</div>
        <div style={{ marginBottom: 6 }}>
          OASIS support status: {qa.supported} supported / {qa.partiallySupported} partial / {qa.unsupported} unsupported /{" "}
          {qa.conflicting} conflicting (of {qa.totalItems} reviewed)
        </div>
        <div style={{ marginBottom: 6 }}>Contradictions remaining: {contradictionsRemaining}</div>
        <div>Documentation gaps remaining: {gapsRemaining}</div>
      </ResultCard>

      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 14px",
          borderRadius: 999,
          border: `1px solid ${statusStyle.border}`,
          background: statusStyle.bg,
          color: statusStyle.text,
          fontSize: 15,
          fontWeight: 900
        }}
      >
        {statusLabel}
      </div>

      <div style={{ fontSize: 12, color: muted, marginTop: 10, lineHeight: 1.6 }}>
        This is an internal documentation support determination based on the supplied documentation. It is not a CMS
        approval, certification, or compliance guarantee. The clinician remains responsible for final clinical and OASIS
        decisions.
      </div>
    </div>
  );
}

function UnifiedResultPanel({
  result,
  expandedIssues,
  setExpandedIssues,
  accentBlue,
  text,
  muted,
  border,
  onCopy,
  onDownload,
  onRerun,
  onCopyIssueText,
  onInsertIssueFix,
  guidedQuestions,
  guidedActive,
  guidedSnapshot,
  guidedStepIndex,
  guidedDraft,
  guidedBusy,
  onGuidedStart,
  onGuidedDraftChange,
  onGuidedBack,
  onGuidedContinue,
  onGuidedCancel
}) {
  if (!result) return null;

  const statusStyle = getSubmissionStatusStyle(result?.submissionDecision?.status);
  const topBlockingIssues = Array.isArray(result?.topBlockingIssues) ? result.topBlockingIssues : [];
  const allIssues = Array.isArray(result?.issues) ? result.issues : [];
  const caughtIssues = Array.isArray(result?.caughtIssues) ? result.caughtIssues : [];
  const intake = result?.intake || null;
  const qaSummary = getQaSummaryCounts(result?.qaSummary || {});
  const codingSupport = Array.isArray(result?.details?.suggestedResponses) ? result.details.suggestedResponses : [];
  const topIssuesTitle = getTopIssuesTitle(topBlockingIssues);
  const additionalIssues = allIssues.slice(topBlockingIssues.length);

  return (
    <div style={{ marginTop: 22 }}>
      <div
        style={{
          marginBottom: 14,
          padding: 18,
          border: `1px solid ${border}`,
          borderRadius: 14,
          background: "#ffffff"
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 700, color: muted, marginBottom: 10 }}>
          {result?.mode === "pre-submit-qa" ? "Pre-Submission QA" : "Documentation Support"}
        </div>

        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 12px",
            borderRadius: 999,
            border: `1px solid ${statusStyle.border}`,
            background: statusStyle.bg,
            color: statusStyle.text,
            fontSize: 14,
            fontWeight: 900,
            marginBottom: 12
          }}
        >
          {statusStyle.label}
        </div>

        <div style={{ fontSize: 26, fontWeight: 900, color: accentBlue, lineHeight: 1.15, marginBottom: 10 }}>
          {getSubmissionHeadline(result?.submissionDecision?.status)}
        </div>

        {result?.submissionDecision?.reason && (
          <div style={{ fontSize: 14, lineHeight: 1.7, color: text, marginBottom: 12 }}>
            {result.submissionDecision.reason}
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 10,
            marginBottom: 12
          }}
        >
          <div
            style={{
              padding: 12,
              borderRadius: 12,
              background: "#f9fafb",
              border: `1px solid ${border}`
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: muted, marginBottom: 4 }}>Reviewed</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: text }}>{qaSummary.totalItems}</div>
          </div>

          <div
            style={{
              padding: 12,
              borderRadius: 12,
              background: "#f9fafb",
              border: `1px solid ${border}`
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: muted, marginBottom: 4 }}>Supported</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: text }}>{qaSummary.supported}</div>
          </div>

          <div
            style={{
              padding: 12,
              borderRadius: 12,
              background: "#f9fafb",
              border: `1px solid ${border}`
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: muted, marginBottom: 4 }}>Partially Supported</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: text }}>{qaSummary.partiallySupported}</div>
          </div>

          <div
            style={{
              padding: 12,
              borderRadius: 12,
              background: "#f9fafb",
              border: `1px solid ${border}`
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: muted, marginBottom: 4 }}>Unsupported</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: text }}>{qaSummary.unsupported}</div>
          </div>

          <div
            style={{
              padding: 12,
              borderRadius: 12,
              background: "#f9fafb",
              border: `1px solid ${border}`
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: muted, marginBottom: 4 }}>Conflicting</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: text }}>{qaSummary.conflicting}</div>
          </div>
        </div>

        {result?.nextStep && (
          <div
            style={{
              padding: 12,
              borderRadius: 12,
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              fontSize: 13,
              lineHeight: 1.65,
              color: "#1e3a8a",
              marginBottom: 12
            }}
          >
            <b>Next step</b>
            <br />
            {result.nextStep}
          </div>
        )}

        <div
          className="rowStack"
          style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}
        >
          <button
            className="fullWidthBtn"
            onClick={onCopy}
            style={{ padding: "13px 14px", borderRadius: 12, cursor: "pointer" }}
          >
            Copy summary
          </button>

          <button
            className="fullWidthBtn"
            onClick={onRerun}
            style={{ padding: "13px 14px", borderRadius: 12, cursor: "pointer" }}
          >
            Re-run after edits
          </button>

          <button
            className="fullWidthBtn"
            onClick={onDownload}
            style={{ padding: "13px 14px", borderRadius: 12, cursor: "pointer" }}
          >
            Download summary
          </button>
        </div>
      </div>

      <div style={{ fontSize: 12, color: muted, lineHeight: 1.6, marginBottom: 14, maxWidth: 640 }}>
        Below: what was found, what's missing, what needs clarification, and what should be
        documented next. Where information is missing, wording stays templated — never treat it as a
        confirmed fact.
      </div>

      {caughtIssues.length > 0 && (
        <ResultCard title="What OasisCopilot caught" titleColor={text} borderColor={border}>
          <SectionList items={caughtIssues} emptyText="(None.)" />
        </ResultCard>
      )}

      {intake && (
        <ResultCard title="Documentation intake status" titleColor={text} borderColor={border}>
          <div style={{ marginBottom: 8 }}>
            <b>Status:</b> {intake.status || "UNKNOWN"}
          </div>

          {Array.isArray(intake.missingDomains) && intake.missingDomains.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <b>Documentation items to review</b>
              <div style={{ marginTop: 6 }}>
                <SectionList items={intake.missingDomains} />
              </div>
            </div>
          )}

          {Array.isArray(intake.prompts) && intake.prompts.length > 0 && (
            <div>
              <b>What to document</b>
              <div style={{ marginTop: 6 }}>
                <SectionList items={intake.prompts} />
              </div>
            </div>
          )}
        </ResultCard>
      )}

      {codingSupport.length > 0 && (
        <ResultCard title="Coding support (based on documentation)" titleColor={text} borderColor={border}>
          <div style={{ fontSize: 13, color: "#374151", marginBottom: 12 }}>
            Suggested responses are shown only when the documentation clearly supports them.
          </div>
          {codingSupport.map((item, idx) => (
            <div
              key={idx}
              style={{
                marginBottom: 12,
                paddingBottom: 12,
                borderBottom: idx < codingSupport.length - 1 ? `1px solid ${border}` : "none"
              }}
            >
              <div style={{ fontWeight: 800, color: text }}>{item.item}</div>
              <div>Suggested response: {item.suggestedResponse}</div>
              {item.supportLevel ? <div>Support level: {normalizeSupportLevel(item.supportLevel)}</div> : null}
              {Array.isArray(item.supportingQuotes) && item.supportingQuotes.length > 0 ? (
                <div>Evidence: {item.supportingQuotes.join(" | ")}</div>
              ) : null}
            </div>
          ))}
        </ResultCard>
      )}

      {topBlockingIssues.length > 0 && (
        <div style={{ marginBottom: 6 }}>
          <div style={{ fontWeight: 900, fontSize: 18, color: text, marginBottom: 10 }}>
            {topIssuesTitle}
          </div>

          {topBlockingIssues.slice(0, 3).map((issue, idx) => {
            const key = `top-${idx}-${issue?.item || "issue"}`;
            const isExpanded = !!expandedIssues[key];

            return (
              <IssueCard
                key={key}
                issue={issue}
                idx={idx}
                isExpanded={isExpanded}
                onToggle={() =>
                  setExpandedIssues((prev) => ({
                    ...prev,
                    [key]: !prev[key]
                  }))
                }
                onCopyText={onCopyIssueText}
                onInsertFix={onInsertIssueFix}
                accentBlue={accentBlue}
                border={border}
              />
            );
          })}
        </div>
      )}

      {additionalIssues.length > 0 && (
        <ResultCard title="Additional issues" titleColor={text} borderColor={border}>
          {additionalIssues.map((issue, idx) => {
            const key = `extra-${idx}-${issue?.item || "issue"}`;
            const isExpanded = !!expandedIssues[key];

            return (
              <IssueCard
                key={key}
                issue={issue}
                idx={idx}
                isExpanded={isExpanded}
                onToggle={() =>
                  setExpandedIssues((prev) => ({
                    ...prev,
                    [key]: !prev[key]
                  }))
                }
                onCopyText={onCopyIssueText}
                onInsertFix={onInsertIssueFix}
                accentBlue={accentBlue}
                border={border}
              />
            );
          })}
        </ResultCard>
      )}

      {result?.details?.followUpQuestions?.length > 0 && (
        <ResultCard title="Follow-up questions" titleColor={text} borderColor={border}>
          <SectionList items={result.details.followUpQuestions} />
        </ResultCard>
      )}

      <GuidedQuestionsPanel
        questions={guidedQuestions}
        active={guidedActive}
        snapshot={guidedSnapshot}
        stepIndex={guidedStepIndex}
        draft={guidedDraft}
        busy={guidedBusy}
        onStart={onGuidedStart}
        onDraftChange={onGuidedDraftChange}
        onBack={onGuidedBack}
        onContinue={onGuidedContinue}
        onCancel={onGuidedCancel}
        accentBlue={accentBlue}
        text={text}
        muted={muted}
        border={border}
      />

      {result?.details?.intake?.recommendedPlacement?.length > 0 && (
        <ResultCard title="Recommended documentation placement" titleColor={text} borderColor={border}>
          {result.details.intake.recommendedPlacement.map((entry, idx) => (
            <div key={idx} style={{ marginBottom: 10 }}>
              <b>{entry.topic}</b>: {entry.place}
            </div>
          ))}
        </ResultCard>
      )}
    </div>
  );
}

function MainApp() {
  const ACCENT_BLUE = "#1d4f7a";
  const TEXT = "#111827";
  const MUTED = "#6b7280";
  const BORDER = "#e5e7eb";
  const FONT_STACK =
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

  const [orgCode, setOrgCode] = useState("");
  const [remember, setRemember] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [authError, setAuthError] = useState("");
  const [isAuthed, setIsAuthed] = useState(false);

  const [toast, setToast] = useState("");
  const [assessmentType, setAssessmentType] = useState("Start of Care");
  const [templateChoice, setTemplateChoice] = useState("general");

  const [showIntake, setShowIntake] = useState(false);
  const [intakeFields, setIntakeFields] = useState(
    INTAKE_FIELDS.reduce((acc, field) => {
      acc[field.key] = "";
      return acc;
    }, {})
  );

  const [notes, setNotes] = useState("");
  const [responsesText, setResponsesText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  // Kept in sync with `result` at render time (see assignment near
  // runReviewRef below). Smart Resolution's insertResolutionFix reads this
  // after awaiting runReview(), since the `result` variable captured in
  // insertResolutionFix's own closure would otherwise be stale -- refs are
  // not subject to that staleness because they're read directly, not
  // through a render-time closure.
  const resultRef = useRef(null);
  const [expandedIssues, setExpandedIssues] = useState({});

  // === Live Review (auto-review) state ===
  // Separate from `loading` so the manual "Run Review" button's own
  // loading label/behavior is never altered by auto-triggered reviews.
  const [isAutoReviewing, setIsAutoReviewing] = useState(false);

  const liveReviewTimerRef = useRef(null);
  const activeAbortControllerRef = useRef(null);
  const activeRequestIdRef = useRef(0);
  // Tracks whether the currently in-flight request (if any) was manual or
  // auto-triggered, so the debounce effect can safely cancel a stale
  // in-flight *auto* request on new input without ever touching a manual one.
  const activeRequestTriggerRef = useRef(null);
  // Holds the latest runReview closure so the debounce effect (which only
  // depends on notes/intake text) always calls it with fresh state
  // (assessmentType, responsesText, orgCode, loading, etc.) without needing
  // those values in its own dependency array.
  const runReviewRef = useRef(null);

  // === Guided Documentation Questions state ===
  // `guidedActive` / `guidedStepIndex` / `guidedAnswers` drive the in-progress
  // guided flow. `guidedSnapshot` freezes the question list at the moment the
  // clinician starts answering, so a background Live Review result arriving
  // mid-flow (Feature 1) can never shift the questions out from under them.
  const [guidedActive, setGuidedActive] = useState(false);
  const [guidedStepIndex, setGuidedStepIndex] = useState(0);
  const [guidedAnswers, setGuidedAnswers] = useState({});
  const [guidedSnapshot, setGuidedSnapshot] = useState([]);
  // Set right before a guided-answer submission programmatically updates
  // `notes`, so the Live Review debounce effect skips exactly one redundant
  // auto-review cycle (the guided submit already triggers its own re-run).
  const skipNextLiveReviewRef = useRef(false);

  // === Complete My Documentation state (Feature 4) ===
  const [docPackageLoading, setDocPackageLoading] = useState(false);
  const [docPackageError, setDocPackageError] = useState("");
  const [docPackageResult, setDocPackageResult] = useState(null);

  // === Narrative Generator state (Feature 5) ===
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [narrativeError, setNarrativeError] = useState("");
  const [narrativeResult, setNarrativeResult] = useState(null);

  // === Smart Resolution Engine state ===
  // Built entirely on top of the existing issue objects (result.issues,
  // already produced by the Review/QA Engines and buildIssueObject) and the
  // existing insertFixIntoNotes/runReview functions. No new detection or
  // validation logic -- this is a resolution workflow layered on the
  // existing detection system, per the explicit requirement not to create a
  // second competing validation engine.
  //
  // resolutionStates is keyed by issue.item (the stable domain display
  // name) and is intentionally NEVER wholesale-reset by an effect tied to
  // `result` -- only explicit clinician actions update it. This avoids a
  // real state-batching hazard: a naive "resync on every result change"
  // effect would run in the same batch as insertResolutionFix's own
  // recheck-verdict update and could overwrite "resolved" /
  // "still_needs_attention" with a blanket "unresolved" reset. Persisting
  // resolutionStates independently of `result` sidesteps that risk
  // entirely, and also means a background Live Review completing mid-flow
  // can never disturb an in-progress resolution (same protective pattern
  // already used for guidedSnapshot in Feature 2).
  const [smartResolutionActive, setSmartResolutionActive] = useState(false);
  // Snapshot of the issue list taken when Smart Resolution is opened, used
  // only for the interactive one-at-a-time panel's ordering. The Final
  // Visit QA Gate below reads result.issues directly instead (always
  // current), so it never depends on this snapshot being fresh.
  const [resolutionQueue, setResolutionQueue] = useState([]);
  const [resolutionStates, setResolutionStates] = useState({});
  const [resolutionDrafts, setResolutionDrafts] = useState({});
  const [resolutionCorrections, setResolutionCorrections] = useState({});
  const [resolutionDetails, setResolutionDetails] = useState({});
  const [resolutionBusy, setResolutionBusy] = useState(false);
  const [activeResolutionItem, setActiveResolutionItem] = useState(null);

  // === Session-only draft recovery ===
  // showDraftRestoreBanner/pendingDraft hold an offered-but-not-yet-applied
  // draft found in sessionStorage on load. Nothing is auto-restored --
  // the clinician explicitly chooses Restore or Discard.
  const [showDraftRestoreBanner, setShowDraftRestoreBanner] = useState(false);
  const [pendingDraft, setPendingDraft] = useState(null);
  const draftSaveTimerRef = useRef(null);
  // Set synchronously (true) the moment a draft is offered for restore,
  // and reset (false) once the clinician restores or discards it. Checked
  // inside the autosave timer's callback at fire time -- see the effects
  // below for why this must be a ref, not state.
  const draftRestorePendingRef = useRef(false);

  // Guards runGeneration responses against Clear/sign-out happening while a
  // request is in flight. Deliberately separate from activeRequestIdRef
  // (Feature 1) -- that counter tracks review requests specifically;
  // sharing it here would make an in-flight generation request look
  // "stale" whenever an unrelated review request starts, and vice versa.
  const generationRequestIdRef = useRef(0);

  const templateTexts = useMemo(() => TEMPLATE_OPTIONS.map((t) => t.text.trim()), []);
  const parsedResponsesPreview = useMemo(() => parseQaResponses(responsesText), [responsesText]);
  const structuredIntakeText = useMemo(() => buildStructuredIntakeText(intakeFields), [intakeFields]);
  const notesLen = notes.trim().length;
  const showBriefHint = notesLen > 0 && notesLen < 60;
  const showLongHint = notesLen > 12000;
  const hasAnyInput = Boolean(notes.trim() || structuredIntakeText.trim());
  // True while any generation request (review, live review, documentation
  // package, or narrative) is in flight -- used to prevent overlapping
  // heavy Claude calls across the standalone generation buttons.
  const generationBusy = loading || isAutoReviewing || docPackageLoading || narrativeLoading;
  // Derived directly from actual intakeFields state -- a real count, not a
  // fabricated completion percentage. Every intake field is optional, so
  // this is presented as progress information, not a required threshold.
  const intakeCompletedCount = INTAKE_FIELDS.filter((field) => intakeFields[field.key].trim().length > 0).length;

  // Live view of the review's follow-up questions — used only to decide
  // whether/how many questions to offer at the collapsed entry point.
  const guidedQuestions = useMemo(
    () => (Array.isArray(result?.details?.followUpQuestions) ? result.details.followUpQuestions : []),
    [result]
  );
  const guidedCurrentQuestion = guidedSnapshot[guidedStepIndex] || "";
  // Raw (untrimmed) value for the controlled textarea — trimming happens
  // only when building the final submitted text via buildGuidedAnswersText.
  const guidedDraft = guidedCurrentQuestion ? guidedAnswers[guidedCurrentQuestion] || "" : "";

  useEffect(() => {
    const stored = getStoredCode();
    if (stored) {
      setOrgCode(stored);
      setRemember(Boolean(localStorage.getItem(LOCAL_KEY)));
      validateCode(stored, Boolean(localStorage.getItem(LOCAL_KEY)), true);
    } else {
      setAuthChecked(true);
      setIsAuthed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set the browser tab favicon from the brand asset. Done in JS rather
  // than via a <link> tag in public/index.html since no such file was
  // visible in the project as provided to work with directly.
  useEffect(() => {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = LOGO_FAVICON_DATA_URI;
  }, []);

  // Offer to restore a previous draft found in sessionStorage, once per
  // login, and only if the form is currently empty -- this never
  // overwrites something the clinician has already started typing this
  // session, and nothing is auto-restored without an explicit choice.
  useEffect(() => {
    if (!isAuthed) return;
    const draft = loadDraftFromSession();
    if (!draft) return;

    const formIsEmpty =
      !notes.trim() &&
      !responsesText.trim() &&
      Object.values(intakeFields).every((v) => !String(v || "").trim());

    if (formIsEmpty) {
      // Set synchronously (a ref, not state) so the autosave effect below
      // can never race ahead of this on the same render pass and clear the
      // draft being offered before the clinician has decided what to do.
      draftRestorePendingRef.current = true;
      setPendingDraft(draft);
      setShowDraftRestoreBanner(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed]);

  // Debounced session-only draft autosave. Stores only the clinician's
  // typed inputs -- assessment type, notes, intake fields, OASIS responses
  // text, template choice -- never AI-generated results, which can be
  // regenerated on demand. The pending-restore check happens inside the
  // timeout callback itself (at fire time), not just at schedule time, so
  // it can never race ahead of the restore-offer effect above regardless
  // of how quickly React re-renders.
  useEffect(() => {
    if (!isAuthed) return undefined;

    if (draftSaveTimerRef.current) {
      clearTimeout(draftSaveTimerRef.current);
    }

    draftSaveTimerRef.current = setTimeout(() => {
      if (draftRestorePendingRef.current) return;

      const hasContent =
        notes.trim() ||
        responsesText.trim() ||
        Object.values(intakeFields).some((v) => String(v || "").trim());

      if (hasContent) {
        saveDraftToSession({ assessmentType, notes, intakeFields, responsesText, templateChoice });
      } else {
        clearDraftFromSession();
      }
    }, 800);

    return () => {
      if (draftSaveTimerRef.current) {
        clearTimeout(draftSaveTimerRef.current);
      }
    };
  }, [isAuthed, showDraftRestoreBanner, assessmentType, notes, intakeFields, responsesText, templateChoice]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 1600);
    return () => clearTimeout(t);
  }, [toast]);

  // === Live Review: debounce auto-trigger ===
  // Watches only the visit-notes textarea and the structured intake fields
  // (never the OASIS responses textarea, per feature scope). Schedules a
  // single auto review call after the clinician stops typing. If a newer
  // edit arrives while a previously auto-triggered request is still in
  // flight, that stale request is aborted immediately rather than letting
  // it resolve and briefly show outdated results.
  useEffect(() => {
    if (liveReviewTimerRef.current) {
      clearTimeout(liveReviewTimerRef.current);
      liveReviewTimerRef.current = null;
    }

    // A guided-answer submission just updated `notes` programmatically and
    // is already triggering its own manual re-run; skip this one cycle so
    // we don't also schedule a redundant auto-review a few seconds later.
    if (skipNextLiveReviewRef.current) {
      skipNextLiveReviewRef.current = false;
      return undefined;
    }

    // New input has arrived: any in-flight *auto* request is now stale.
    // Manual reviews are left alone; the clinician explicitly requested those.
    if (activeAbortControllerRef.current && activeRequestTriggerRef.current === "auto") {
      activeAbortControllerRef.current.abort();
      activeAbortControllerRef.current = null;
      setIsAutoReviewing(false);
    }

    const combinedLength = notesLen + structuredIntakeText.trim().length;
    if (combinedLength < LIVE_REVIEW_MIN_CONTENT_LENGTH) {
      return undefined;
    }

    liveReviewTimerRef.current = setTimeout(() => {
      liveReviewTimerRef.current = null;
      runReviewRef.current?.("auto");
    }, LIVE_REVIEW_DEBOUNCE_MS);

    return () => {
      if (liveReviewTimerRef.current) {
        clearTimeout(liveReviewTimerRef.current);
        liveReviewTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes, structuredIntakeText, notesLen]);

  // Cleanup on unmount only: unconditionally abort any in-flight request
  // (manual or auto) so nothing resolves against an unmounted component.
  // (Timer clearing is already handled by the debounce effect's own
  // cleanup above, which also runs on unmount — no need to repeat it here.)
  useEffect(() => {
    return () => {
      if (activeAbortControllerRef.current) {
        activeAbortControllerRef.current.abort();
      }
    };
  }, []);

  function setIntakeField(key, value) {
    setIntakeFields((prev) => ({ ...prev, [key]: value }));
    setResult(null);
    setError("");
    setExpandedIssues({});
    invalidateGeneratedResults();
  }

  function getSelectedTemplateText(choice) {
    const selected = TEMPLATE_OPTIONS.find((t) => t.value === choice) || TEMPLATE_OPTIONS[0];
    return selected.text;
  }

  function insertTemplate() {
    setNotes(getSelectedTemplateText(templateChoice));
    setResult(null);
    setError("");
    setExpandedIssues({});
    invalidateGeneratedResults();
  }

  function handleTemplateChange(newChoice) {
    setTemplateChoice(newChoice);
    const current = (notes || "").trim();
    const safeToReplace = !current || templateTexts.includes(current);

    if (safeToReplace) {
      setNotes(getSelectedTemplateText(newChoice));
      setResult(null);
      setError("");
      setExpandedIssues({});
      invalidateGeneratedResults();
    }
  }

  function clearReview() {
    // Live Review: cancel any pending debounce timer and in-flight request
    // so a stale auto-review can't repopulate the form right after clearing.
    if (liveReviewTimerRef.current) {
      clearTimeout(liveReviewTimerRef.current);
      liveReviewTimerRef.current = null;
    }
    if (activeAbortControllerRef.current) {
      activeAbortControllerRef.current.abort();
      activeAbortControllerRef.current = null;
      activeRequestTriggerRef.current = null;
    }
    setIsAutoReviewing(false);

    // Guided Documentation Questions: reset in-progress flow.
    resetGuidedState();

    // Smart Resolution Engine: reset in-progress flow.
    setSmartResolutionActive(false);
    setResolutionQueue([]);
    setResolutionStates({});
    setResolutionDrafts({});
    setResolutionCorrections({});
    setResolutionDetails({});
    setResolutionBusy(false);
    setActiveResolutionItem(null);

    // Complete My Documentation / Narrative Generator: reset results and
    // invalidate any in-flight request so a late-arriving response can't
    // silently repopulate the form after it's been cleared.
    invalidateGeneratedResults();

    setNotes("");
    setResponsesText("");
    setResult(null);
    setError("");
    setExpandedIssues({});
    setIntakeFields(
      INTAKE_FIELDS.reduce((acc, field) => {
        acc[field.key] = "";
        return acc;
      }, {})
    );

    // Session-only draft recovery: clearing the form clears the stored
    // draft too, so a stale draft can't resurface after a refresh.
    if (draftSaveTimerRef.current) {
      clearTimeout(draftSaveTimerRef.current);
      draftSaveTimerRef.current = null;
    }
    clearDraftFromSession();
    draftRestorePendingRef.current = false;
    setShowDraftRestoreBanner(false);
    setPendingDraft(null);
  }

  function restoreDraft() {
    if (!pendingDraft) return;

    if (typeof pendingDraft.assessmentType === "string") setAssessmentType(pendingDraft.assessmentType);
    if (typeof pendingDraft.notes === "string") setNotes(pendingDraft.notes);
    if (pendingDraft.intakeFields && typeof pendingDraft.intakeFields === "object") {
      setIntakeFields((prev) => ({ ...prev, ...pendingDraft.intakeFields }));
    }
    if (typeof pendingDraft.responsesText === "string") setResponsesText(pendingDraft.responsesText);
    if (typeof pendingDraft.templateChoice === "string") setTemplateChoice(pendingDraft.templateChoice);

    draftRestorePendingRef.current = false;
    setShowDraftRestoreBanner(false);
    setPendingDraft(null);
    setToast("Draft restored.");
  }

  function discardDraft() {
    clearDraftFromSession();
    draftRestorePendingRef.current = false;
    setShowDraftRestoreBanner(false);
    setPendingDraft(null);
  }

  async function validateCode(inputCode, rememberChoice, silent = false) {
    const codeNorm = normalizeCode(inputCode);

    if (!codeNorm) {
      if (!silent) setAuthError("Code required.");
      setAuthChecked(true);
      setIsAuthed(false);
      return;
    }

    setAuthError("");
    if (!silent) setAuthChecked(false);

    try {
      const r = await fetch(`${API_BASE}/api/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: codeNorm })
      });

      if (r.status === 401) {
        setAuthError("Code required.");
        setAuthChecked(true);
        setIsAuthed(false);
        return;
      }
      if (r.status === 403) {
        setAuthError("Invalid code. Please confirm with your agency administrator.");
        setAuthChecked(true);
        setIsAuthed(false);
        return;
      }
      if (r.status === 429) {
        setAuthError("Too many attempts. Please wait and try again.");
        setAuthChecked(true);
        setIsAuthed(false);
        return;
      }
      if (!r.ok) {
        setAuthError("Service unavailable. Please try again later.");
        setAuthChecked(true);
        setIsAuthed(false);
        return;
      }

      storeCode(codeNorm, rememberChoice);
      setOrgCode(codeNorm);
      setRemember(rememberChoice);
      setAuthChecked(true);
      setIsAuthed(true);
    } catch {
      setAuthError("Service unavailable. Please try again later.");
      setAuthChecked(true);
      setIsAuthed(false);
    }
  }

  function signOut() {
    clearStoredCode();
    setIsAuthed(false);
    setAuthChecked(true);
    setOrgCode("");
    setRemember(false);
    setAuthError("");
    setToast("");
    clearReview();
  }

  async function runReview(trigger = "manual", options = {}) {
    const isAuto = trigger === "auto";

    // A manual review always takes precedence: if one is already in flight,
    // don't let an auto-trigger start a competing request. `loading` here
    // reflects the current render's state because runReviewRef is
    // reassigned to this closure on every render (see below).
    if (isAuto && loading) return false;

    // Never let an auto-triggered review overlap a manual one, or vice versa.
    // Cancel any in-flight request (manual or auto) before starting a new one
    // so a slower, older response can never overwrite a newer result.
    if (activeAbortControllerRef.current) {
      activeAbortControllerRef.current.abort();
    }

    const controller = new AbortController();
    activeAbortControllerRef.current = controller;
    activeRequestTriggerRef.current = trigger;

    const requestId = activeRequestIdRef.current + 1;
    activeRequestIdRef.current = requestId;

    // Guided-answer submissions pass the freshly updated notes text directly,
    // avoiding a stale-closure read of `notes` before its own setNotes()
    // call has had a chance to flow through a re-render. Every other caller
    // (manual button, Live Review debounce) omits this and gets current
    // behavior unchanged.
    const notesForRequest = options.notesOverride !== undefined ? options.notesOverride : notes;
    const mergedNotes = buildSubmissionPayloadNotes({ intake: intakeFields, notes: notesForRequest });

    if (isAuto) {
      setIsAutoReviewing(true);
    } else {
      setLoading(true);
      // Preserve existing manual-trigger behavior exactly: clear the
      // previous result immediately on click.
      setResult(null);
    }
    setError("");
    setExpandedIssues({});

    // Tracks whether a result was actually applied by this call. Existing
    // callers (manual button, Live Review debounce) ignore the return value
    // entirely, so this is purely additive — it exists so callers like
    // Guided Documentation Questions can know whether it's safe to clear
    // their own state, or whether the request failed and their input
    // (e.g. typed answers) should be preserved for a retry.
    let succeeded = false;

    try {
      const r = await fetch(`${API_BASE}/api/pre-submit-review`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-org-code": normalizeCode(orgCode)
        },
        body: JSON.stringify({
          assessmentType,
          notes: mergedNotes,
          responses: parsedResponsesPreview || undefined,
          modeOverride: parsedResponsesPreview ? "qa" : "documentation"
        }),
        signal: controller.signal
      });

      const d = await r.json();

      // Ignore this response if a newer request has since started.
      if (activeRequestIdRef.current !== requestId) return false;

      if (r.status === 401) setError("Code required. Please enter your organization access code.");
      else if (r.status === 403) setError("Invalid code. Please confirm with your agency administrator.");
      else if (!r.ok) setError(d?.details || d?.error || "Service unavailable. Please try again later.");
      else {
        setResult(d?.result || null);
        succeeded = true;
      }
    } catch (err) {
      // Aborted requests (superseded by newer input) are expected and silent.
      if (err?.name === "AbortError") return false;
      if (activeRequestIdRef.current !== requestId) return false;
      setError("Service unavailable. Please try again later.");
    }

    if (activeRequestIdRef.current === requestId) {
      activeRequestTriggerRef.current = null;
      if (isAuto) {
        setIsAutoReviewing(false);
      } else {
        setLoading(false);
      }
    }

    return succeeded;
  }

  // Keep a ref to the latest runReview closure so the debounce effect below
  // can always call it with current state without listing every dependency.
  runReviewRef.current = runReview;
  resultRef.current = result;

  // === Guided Documentation Questions handlers ===
  // Single reset point, reused by cancel / clearReview / a successful submit,
  // instead of repeating the same four setState calls in three places.
  function resetGuidedState() {
    setGuidedActive(false);
    setGuidedStepIndex(0);
    setGuidedAnswers({});
    setGuidedSnapshot([]);
  }

  // Single reset point for Complete My Documentation / Narrative Generator
  // results, reused by clearReview() and by every place notes/intake
  // content changes (a generated package/narrative is only valid for the
  // exact content it was generated from). Also invalidates any in-flight
  // request and resets the busy flags -- without resetting busy here, a
  // response arriving after this call would be silently ignored by
  // runGeneration's staleness check but would never flip loading back to
  // false, leaving the button stuck showing "Generating…".
  function invalidateGeneratedResults() {
    generationRequestIdRef.current += 1;
    setDocPackageLoading(false);
    setDocPackageError("");
    setDocPackageResult(null);
    setNarrativeLoading(false);
    setNarrativeError("");
    setNarrativeResult(null);
  }

  function startGuidedQuestions() {
    setGuidedSnapshot(guidedQuestions);
    setGuidedAnswers({});
    setGuidedStepIndex(0);
    setGuidedActive(true);
  }

  function updateGuidedDraft(value) {
    if (!guidedCurrentQuestion) return;
    setGuidedAnswers((prev) => ({ ...prev, [guidedCurrentQuestion]: value }));
  }

  function goGuidedBack() {
    setGuidedStepIndex((idx) => Math.max(0, idx - 1));
  }

  function cancelGuidedQuestions() {
    resetGuidedState();
  }

  async function submitGuidedAnswers() {
    const guidedText = buildGuidedAnswersText(guidedSnapshot, guidedAnswers);
    const baseNotes = notes.trim();

    const updatedNotes = guidedText
      ? baseNotes
        ? `${notes}\n\nGuided Documentation Answers:\n${guidedText}`
        : `Guided Documentation Answers:\n${guidedText}`
      : notes;

    // Note: the guided panel is intentionally left active (not reset here)
    // while the request is in flight. That keeps it showing the "Working…"
    // busy state on the Submit button, and if the request fails, the
    // clinician's answers stay intact so they can retry without redoing
    // the whole flow. State is only reset below on confirmed success.

    if (updatedNotes !== notes) {
      // Prevent the Live Review debounce effect from also scheduling a
      // redundant auto-review off the back of this same notes update.
      skipNextLiveReviewRef.current = true;
      setNotes(updatedNotes);
      invalidateGeneratedResults();
      setToast("Answers added to documentation. Re-running review…");
    }

    const succeeded = await runReview("manual", { notesOverride: updatedNotes });

    if (succeeded) {
      resetGuidedState();
    }
  }

  // Appends a Review/QA issue's copy-ready wording to the working notes,
  // completely unmodified -- never alters, completes, or interprets the
  // text (the existing "do not guess" clinical safety rule is preserved
  // by construction: placeholders like [level of assistance] are inserted
  // exactly as-is for the clinician to complete).
  function insertFixIntoNotes(text) {
    const cleanText = typeof text === "string" ? text.trim() : "";
    if (!cleanText) return null;

    // Exact-text duplicate protection only (no fuzzy matching): if this
    // wording is already present in notes, do nothing rather than insert
    // it a second time.
    if (notes.includes(cleanText)) {
      setToast("Already in your note.");
      return null;
    }

    const baseNotes = notes.trim();
    const updatedNotes = baseNotes ? `${notes}\n\n${cleanText}` : cleanText;

    // Deliberately mirrors the notes textarea's own onChange handler
    // exactly -- Insert Fix is treated as an ordinary change to the
    // working notes, with the same existing downstream effects (clearing
    // the current review result, invalidating generated documentation/
    // narrative, and leaving Live Review's existing debounce to run again
    // naturally). No new behavior is introduced for these systems.
    setNotes(updatedNotes);
    setResult(null);
    setError("");
    setExpandedIssues({});
    invalidateGeneratedResults();
    setToast("Inserted into note.");
    return updatedNotes;
  }

  // === Smart Resolution Engine handlers ===
  function startSmartResolution() {
    if (!result) return;
    const issues = Array.isArray(result.issues) ? result.issues : [];
    setResolutionQueue(issues);
    setActiveResolutionItem(issues.length ? issues[0].item : null);
    setSmartResolutionActive(true);
  }

  function closeSmartResolution() {
    setSmartResolutionActive(false);
  }

  function selectResolutionIssue(item) {
    setActiveResolutionItem(item);
    setResolutionStates((prev) => ({
      ...prev,
      [item]: prev[item] === RESOLUTION_STATE.RESOLVED ? prev[item] : RESOLUTION_STATE.AWAITING_INPUT
    }));
  }

  function updateResolutionOptionDraft(item, value) {
    setResolutionDrafts((prev) => ({ ...prev, [item]: { ...(prev[item] || {}), selectedValue: value } }));
  }

  function updateResolutionFreeTextDraft(item, value) {
    setResolutionDrafts((prev) => ({ ...prev, [item]: { ...(prev[item] || {}), freeText: value } }));
  }

  // ASK -> the minimum question necessary. Structured (resolutionOptions,
  // backend-provided) or free-text fallback built from the issue's own
  // `required` fields -- never a fixed universal question.
  function continueResolution(issue) {
    const draft = resolutionDrafts[issue.item] || {};
    const hasOptions = Boolean(
      issue.resolutionOptions && Array.isArray(issue.resolutionOptions.options) && issue.resolutionOptions.options.length
    );

    if (hasOptions) {
      const chosen = issue.resolutionOptions.options.find((o) => o.value === draft.selectedValue);
      if (!chosen) return;

      // "Insufficient information" (or any option with no correctionText)
      // never proceeds to a suggested correction. The system does not
      // guess -- the issue stays explicitly unresolved.
      if (!chosen.correctionText) {
        setResolutionStates((prev) => ({ ...prev, [issue.item]: RESOLUTION_STATE.STILL_NEEDS_ATTENTION }));
        setResolutionDetails((prev) => ({
          ...prev,
          [issue.item]: "Insufficient information provided. This issue remains unresolved."
        }));
        return;
      }

      setResolutionCorrections((prev) => ({ ...prev, [issue.item]: chosen.correctionText }));
      setResolutionStates((prev) => ({ ...prev, [issue.item]: RESOLUTION_STATE.READY_TO_INSERT }));
      return;
    }

    const freeText = formatValue(draft.freeText);
    if (!freeText) {
      setResolutionStates((prev) => ({ ...prev, [issue.item]: RESOLUTION_STATE.STILL_NEEDS_ATTENTION }));
      setResolutionDetails((prev) => ({
        ...prev,
        [issue.item]: "Insufficient information provided. This issue remains unresolved."
      }));
      return;
    }

    const base = issue.copyText ? `${issue.copyText}\n\n` : "";
    setResolutionCorrections((prev) => ({ ...prev, [issue.item]: `${base}${freeText}` }));
    setResolutionStates((prev) => ({ ...prev, [issue.item]: RESOLUTION_STATE.READY_TO_INSERT }));
  }

  function updateResolutionCorrectionText(item, value) {
    setResolutionCorrections((prev) => ({ ...prev, [item]: value }));
  }

  // FIX -> INSERT -> RECHECK -> VERIFY. Reuses insertFixIntoNotes (the
  // existing Insert Fix function) for the actual insert, and runReview (the
  // existing review pipeline, same one Live Review and Guided Questions
  // already use) for the recheck -- no new validation engine.
  async function insertResolutionFix(issue) {
    const correction = formatValue(resolutionCorrections[issue.item]);
    if (!correction) return;

    setResolutionStates((prev) => ({ ...prev, [issue.item]: RESOLUTION_STATE.INSERTED }));

    const updatedNotes = insertFixIntoNotes(correction);

    if (!updatedNotes) {
      // insertFixIntoNotes returns null for a no-op (empty text, or an
      // exact duplicate already present in notes) -- nothing changed, so
      // there is nothing new to recheck.
      setResolutionStates((prev) => ({ ...prev, [issue.item]: RESOLUTION_STATE.READY_TO_INSERT }));
      return;
    }

    setResolutionStates((prev) => ({ ...prev, [issue.item]: RESOLUTION_STATE.RECHECKING }));
    setResolutionBusy(true);

    const succeeded = await runReview("manual", { notesOverride: updatedNotes });

    setResolutionBusy(false);

    if (!succeeded) {
      setResolutionStates((prev) => ({ ...prev, [issue.item]: RESOLUTION_STATE.STILL_NEEDS_ATTENTION }));
      setResolutionDetails((prev) => ({
        ...prev,
        [issue.item]: "Recheck could not be completed. Please try again."
      }));
      return;
    }

    // Read via resultRef, not the `result` variable in this closure -- by
    // the time this async function resumes after awaiting runReview(), a
    // fresh insertResolutionFix closure (with the new `result`) has not
    // necessarily replaced this one. resultRef is kept in sync at render
    // time (see `resultRef.current = result;` near runReviewRef), so it
    // reliably reflects the latest value here.
    const freshIssues = Array.isArray(resultRef.current?.issues) ? resultRef.current.issues : [];
    const stillPresent = freshIssues.find((i) => i.item === issue.item);

    if (stillPresent) {
      // Uses the FRESH issue's own current explanation, not a repeated
      // generic warning -- the notes changed, so this reflects what is
      // actually still missing now.
      setResolutionStates((prev) => ({ ...prev, [issue.item]: RESOLUTION_STATE.STILL_NEEDS_ATTENTION }));
      setResolutionDetails((prev) => ({
        ...prev,
        [issue.item]: stillPresent.problem || "This issue is still present in the current documentation."
      }));
    } else {
      setResolutionStates((prev) => ({ ...prev, [issue.item]: RESOLUTION_STATE.RESOLVED }));
      setResolutionDetails((prev) => ({ ...prev, [issue.item]: "" }));
    }
  }

  function retryResolution(item) {
    setResolutionStates((prev) => ({ ...prev, [item]: RESOLUTION_STATE.AWAITING_INPUT }));
    setResolutionDrafts((prev) => ({ ...prev, [item]: {} }));
    setResolutionCorrections((prev) => ({ ...prev, [item]: "" }));
    setResolutionDetails((prev) => ({ ...prev, [item]: "" }));
  }

  function advanceToNextResolutionIssue() {
    const idx = resolutionQueue.findIndex((i) => i.item === activeResolutionItem);
    const ordered =
      idx === -1 ? resolutionQueue : resolutionQueue.slice(idx + 1).concat(resolutionQueue.slice(0, idx + 1));
    const next = ordered.find(
      (i) => i.item !== activeResolutionItem && resolutionStates[i.item] !== RESOLUTION_STATE.RESOLVED
    );
    setActiveResolutionItem(next ? next.item : null);
  }

  function continueGuidedQuestions() {
    const isLast = guidedStepIndex === guidedSnapshot.length - 1;
    if (!isLast) {
      setGuidedStepIndex((idx) => idx + 1);
      return;
    }
    submitGuidedAnswers();
  }

  // === Shared request lifecycle for standalone generation actions ===
  // Complete My Documentation and Narrative Generator both POST
  // { assessmentType, notes } to their own endpoint and follow the exact
  // same success/error handling as every other request in this app
  // (401/403/other-error/success). One shared function instead of writing
  // the same fetch block twice.
  async function runGeneration(endpoint, { setBusy, setErr, setRes, extraPayload }) {
    if (generationBusy) return;

    const requestId = generationRequestIdRef.current + 1;
    generationRequestIdRef.current = requestId;

    const mergedNotes = buildSubmissionPayloadNotes({ intake: intakeFields, notes });

    setBusy(true);
    setErr("");
    setRes(null);

    try {
      const r = await fetch(`${API_BASE}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-org-code": normalizeCode(orgCode)
        },
        body: JSON.stringify({ assessmentType, notes: mergedNotes, ...extraPayload })
      });

      const d = await r.json();

      // Ignore this response if Clear/sign-out or a newer generation
      // request has since superseded it.
      if (generationRequestIdRef.current !== requestId) return;

      if (r.status === 401) setErr("Code required. Please enter your organization access code.");
      else if (r.status === 403) setErr("Invalid code. Please confirm with your agency administrator.");
      else if (!r.ok) setErr(d?.details || d?.error || "Service unavailable. Please try again later.");
      else setRes(d?.result || null);
    } catch {
      if (generationRequestIdRef.current !== requestId) return;
      setErr("Service unavailable. Please try again later.");
    }

    if (generationRequestIdRef.current === requestId) {
      setBusy(false);
    }
  }

  function generateDocumentationPackage() {
    // Only include guidedAnswers when there is genuinely unsubmitted
    // content to add. buildGuidedAnswersText is the same helper used to
    // bake answers into `notes` on submit, so comparing its output against
    // the current `notes` text is a reliable way to detect whether this
    // exact content has already been incorporated there (submitted
    // successfully, or submitted and failed but already appended) --
    // in either case, sending it separately would duplicate it in the
    // generated documentation. Only genuinely in-progress, not-yet-baked-in
    // answers are sent.
    const currentGuidedText = buildGuidedAnswersText(guidedSnapshot, guidedAnswers);
    const alreadyInNotes = Boolean(currentGuidedText) && notes.includes(currentGuidedText);
    const shouldSendGuidedAnswers = Boolean(currentGuidedText) && !alreadyInNotes;

    return runGeneration("/api/generate-documentation", {
      setBusy: setDocPackageLoading,
      setErr: setDocPackageError,
      setRes: setDocPackageResult,
      extraPayload: shouldSendGuidedAnswers ? { guidedAnswers } : undefined
    });
  }

  function generateNarrative() {
    return runGeneration("/api/generate-narrative", {
      setBusy: setNarrativeLoading,
      setErr: setNarrativeError,
      setRes: setNarrativeResult
    });
  }

  useDocumentSEO({
    title: "OasisCopilot | OASIS-E2 Home Health Documentation Software",
    description:
      "OasisCopilot is OASIS-E2 home health documentation software that helps clinicians and QA teams review, resolve, and complete visit documentation, then finish with a final visit QA check. No training or demo required.",
    canonicalPath: "/",
    noindex: isAuthed,
    jsonLd: isAuthed
      ? null
      : {
          "@context": "https://schema.org",
          "@graph": [buildOrganizationJsonLd(), buildSoftwareApplicationJsonLd(), buildWebSiteJsonLd()]
        }
  });

  if (!authChecked || !isAuthed) {
    return (
      <div style={{ background: "#ffffff", minHeight: "100vh" }}>
        <style>{`
          @media (max-width: 640px) {
            .pricingGrid { flex-direction: column !important; }
            .heroTitle { font-size: 30px !important; }
            .heroSub { font-size: 16px !important; }
            .landingSection { margin-bottom: 18px !important; }
            .accessWrap { padding: 16px !important; }
            .codeInputRow { flex-direction: column !important; align-items: stretch !important; }
            .codeInput { max-width: none !important; width: 100% !important; }
            .fullMobile { width: 100% !important; max-width: none !important; }
            .heroGrid { flex-direction: column !important; align-items: stretch !important; gap: 28px !important; }
            .heroGrid > div { flex-basis: auto !important; max-width: none !important; margin: 0 !important; }
            .navLinks { display: none !important; }
            .workflowRow > div { border-right: none !important; border-bottom: 1px solid ${ENT_BORDER} !important; padding: 20px 0 20px !important; }
            .simplicityRow > div { border-left: none !important; border-top: 1px solid ${ENT_BORDER} !important; padding: 18px 0 0 !important; margin-top: 4px !important; }
            .audienceRow > div { border-right: none !important; margin-right: 0 !important; border-bottom: 1px solid ${ENT_BORDER} !important; padding: 22px 0 !important; margin-bottom: 4px !important; }
            .capabilitiesGrid { gap: 0 !important; }
          }
        `}</style>

        <EnterpriseNav />

        <div
          style={{
            maxWidth: 1160,
            margin: "0 auto",
            padding: "28px 16px 40px",
            fontFamily: FONT_STACK,
            color: ENT_TEXT
          }}
        >
          {/* === Hero (includes the large product demonstration) === */}
          <LandingHero />

          {/* === Trust strip === */}
          <TrustStrip />

          {/* === No training / no demo reinforcement === */}
          <SimplicityBanner />

          {/* === Audience section === */}
          <div id="audience">
            <AudienceSection />
          </div>

          {/* === Broader workflow (marketing level only) === */}
          <div id="workflow">
            <WorkflowSequenceSection />
          </div>

          {/* === Core capabilities === */}
          <div id="capabilities">
            <CapabilitiesSection />
          </div>

          {/* === Product experience showcase === */}
          <ProductShowcaseSection />

          {/* === Trust / credibility === */}
          <div id="trust">
            <TrustSection />
          </div>

          {/* === Trust / data handling (kept compact, removes a purchase objection) === */}
          <div
            className="landingSection"
            style={{
              marginTop: 40,
              marginBottom: 40,
              padding: 14,
              borderRadius: 14,
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              maxWidth: 620,
              fontSize: 12.5,
              color: "#166534",
              lineHeight: 1.6
            }}
          >
            <b style={{ color: "#14532d" }}>How your data is handled.</b> Pasted text is processed to
            generate results and is not stored on our servers. Use deidentified documentation only.
          </div>

          {/* === Pricing === */}
          <div id="pricing" style={{ marginBottom: 56, padding: "60px 40px", borderRadius: 12, background: ENT_NAVY }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: "#7EE0D8", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 14 }}>
              Pricing
            </div>
            <h2 style={{ margin: "0 0 10px", fontSize: 30, fontWeight: 700, color: "#ffffff" }}>
              Subscribe to OasisCopilot
            </h2>
            <div style={{ fontSize: 15, color: "#B0C2D2", marginBottom: 40, maxWidth: 620, lineHeight: 1.65 }}>
              This is paid clinical documentation software. There is no free trial. Use OasisCopilot
              on a real chart and see exactly what it flags before submission.
            </div>

            <div className="pricingGrid" style={{ display: "flex", gap: 16, alignItems: "stretch", flexWrap: "wrap" }}>
              <PricingCard
                title="Clinician"
                price="$59"
                period="/month"
                description="For individual clinicians and reviewers who want a final documentation check before QA."
                bullets={[
                  "Catch missing support before QA reviews the chart",
                  "Strengthen documentation for selected responses",
                  "Use during SOC, recert, and discharge review"
                ]}
                href={STRIPE_INDIVIDUAL_URL}
                buttonText="Start Clinician Access"
                accentBlue={ENT_BLUE}
                border={ENT_BORDER}
                muted={ENT_MUTED}
              />

              <PricingCard
                title="Agency Team"
                price="$149"
                period="/month"
                description="For agencies that want a consistent step to review documentation before submission across clinicians and reviewers."
                bullets={[
                  "Catch unsupported OASIS responses before submission",
                  "Reduce avoidable QA rework and clarification loops",
                  "Standardize documentation review across clinicians"
                ]}
                href={STRIPE_AGENCY_MONTHLY_URL}
                buttonText="Start Agency Access"
                accentBlue={ENT_BLUE}
                border={ENT_BORDER}
                muted={ENT_MUTED}
                highlighted
                badge="Most common"
              />

              <PricingCard
                title="Agency Team Annual"
                price="$1,999"
                period="/year"
                description="For agencies ready to use OasisCopilot across the team year round."
                bullets={[
                  "Everything in Agency Team",
                  "Best value for ongoing agency use",
                  "Includes 2 months free"
                ]}
                href={STRIPE_AGENCY_ANNUAL_URL}
                buttonText="Start Annual Access"
                accentBlue={ENT_BLUE}
                border={ENT_BORDER}
                muted={ENT_MUTED}
                badge="Best value"
              />
            </div>

            <div style={{ marginTop: 14, fontSize: 12, color: "#94a3b8", lineHeight: 1.6 }}>
              Payment is handled securely by Stripe.
            </div>
          </div>

          {/* === Sign in === */}
          <div id="sign-in" style={{ marginBottom: 10, fontSize: 15, fontWeight: 800, color: ENT_TEXT }}>
            Already have an access code?
          </div>

          <div
            className="accessWrap landingSection"
            style={{
              background: "#fbfbfc",
              border: `1px solid ${ENT_BORDER}`,
              borderRadius: 16,
              padding: 16,
              marginBottom: 28,
              maxWidth: 520
            }}
          >
            <div style={{ fontSize: 13, color: ENT_MUTED, marginBottom: 12, lineHeight: 1.6 }}>
              Sign in with the code provided after purchase or by your agency administrator.
            </div>

            <label style={{ fontWeight: 700, display: "block", marginBottom: 8, fontSize: 13 }}>
              Access code
            </label>

            <div className="codeInputRow" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <input
                className="codeInput"
                value={orgCode}
                onChange={(e) => setOrgCode(e.target.value)}
                placeholder="Enter code"
                aria-label="Access code"
                style={{
                  width: "100%",
                  maxWidth: 340,
                  minHeight: 46,
                  padding: "11px 12px",
                  borderRadius: 12,
                  border: `1px solid ${ENT_BORDER}`,
                  fontSize: 14,
                  outline: "none",
                  boxSizing: "border-box"
                }}
              />

              <button
                className="fullMobile"
                onClick={() => validateCode(orgCode, remember, false)}
                style={{
                  padding: "13px 16px",
                  minHeight: 46,
                  borderRadius: 12,
                  background: ENT_BLUE,
                  color: "#fff",
                  border: "none",
                  cursor: "pointer",
                  fontWeight: 800,
                  minWidth: 140
                }}
              >
                Sign in
              </button>
            </div>

            <label
              style={{
                fontSize: 13,
                color: "#374151",
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginTop: 12
              }}
            >
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
              Remember this device
            </label>

            {authError && (
              <div
                role="alert"
                style={{
                  marginTop: 12,
                  padding: 10,
                  background: "#f9fafb",
                  border: `1px solid ${ENT_BORDER}`,
                  borderRadius: 12,
                  fontSize: 13
                }}
              >
                {authError}
              </div>
            )}
          </div>

          {/* === FAQ === */}
          <div className="landingSection" style={{ marginBottom: 20, maxWidth: 760 }}>
            <div style={{ fontWeight: 900, fontSize: 20, color: ENT_TEXT, marginBottom: 14 }}>
              Frequently asked questions
            </div>

            <FAQItem
              question="Does OasisCopilot replace clinical judgment?"
              answer="OasisCopilot identifies missing, unclear, or unsupported documentation and helps prepare drafts for your review. The clinician remains responsible for the final clinical and OASIS determination."
              text={ENT_TEXT}
              border={ENT_BORDER}
            />
            <FAQItem
              question="Can I generate and edit documentation drafts?"
              answer="Yes. OasisCopilot can generate a structured documentation draft and a narrative draft from the information you provide, and every draft is meant to be reviewed and edited before use."
              text={ENT_TEXT}
              border={ENT_BORDER}
            />
            <FAQItem
              question="Does OasisCopilot integrate with my EHR?"
              answer="Not currently. OasisCopilot is used independently, and documentation is copied into your existing systems."
              text={ENT_TEXT}
              border={ENT_BORDER}
            />
            <FAQItem
              question="Can I submit the generated content without reviewing it?"
              answer="Generated content, including drafts and wording you can copy, must be reviewed by the clinician before submission. Where information is missing, wording stays templated rather than being presented as a definitive fact."
              text={ENT_TEXT}
              border={ENT_BORDER}
            />
          </div>

          {/* === Final CTA === */}
          <div
            style={{
              marginBottom: 40,
              padding: "40px 28px",
              borderRadius: 20,
              background: ENT_NAVY,
              textAlign: "center"
            }}
          >
            <div style={{ fontSize: 26, fontWeight: 800, color: "#ffffff", marginBottom: 10 }}>
              Finish documentation faster.
            </div>
            <div style={{ fontSize: 15, color: "#B9C9D8", marginBottom: 22 }}>
              Get started with OasisCopilot today.
            </div>
            <StartReviewCTA>Start a Review</StartReviewCTA>
          </div>

          <FooterLinks showAuthedLinks={false} signOut={signOut} accentBlue={ENT_BLUE} muted={ENT_MUTED} />
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        maxWidth: 980,
        margin: "0 auto",
        padding: "28px 14px",
        fontFamily: FONT_STACK,
        lineHeight: 1.6,
        color: TEXT
      }}
    >
      <style>{`
        @media (max-width: 640px) {
          .rowStack { flex-direction: column !important; align-items: stretch !important; }
          .fullWidthBtn { width: 100% !important; }
          .controlSelect { width: 100% !important; }
          .appTitle { font-size: 28px !important; }
          .intakeGrid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {toast && (
        <div
          style={{
            marginBottom: 12,
            padding: 10,
            border: `1px solid ${BORDER}`,
            background: "#f9fafb",
            borderRadius: 12
          }}
        >
          {toast}
        </div>
      )}

      {showDraftRestoreBanner && (
        <div
          style={{
            marginBottom: 16,
            padding: 14,
            border: "1px solid #bfdbfe",
            background: "#eff6ff",
            borderRadius: 14,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap"
          }}
        >
          <div style={{ fontSize: 13, color: "#1e3a8a", lineHeight: 1.6 }}>
            <b>Draft found from this session.</b> Would you like to restore your previous documentation and
            selections?
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              onClick={restoreDraft}
              style={{
                padding: "9px 14px",
                borderRadius: 10,
                cursor: "pointer",
                border: "none",
                background: ACCENT_BLUE,
                color: "#fff",
                fontWeight: 800,
                fontSize: 13
              }}
            >
              Restore draft
            </button>
            <button
              onClick={discardDraft}
              style={{
                padding: "9px 14px",
                borderRadius: 10,
                cursor: "pointer",
                border: "1px solid #bfdbfe",
                background: "#fff",
                color: "#1e3a8a",
                fontWeight: 800,
                fontSize: 13
              }}
            >
              Discard
            </button>
          </div>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
        <img src={LOGO_BADGE_DATA_URI} alt="" style={{ height: 40, width: 40, borderRadius: 10 }} />
        <h2
          className="appTitle"
          style={{ margin: 0, color: TEXT, fontWeight: 900, fontSize: 32, lineHeight: 1.15 }}
        >
          Review documentation before submission
        </h2>
      </div>

      <div style={{ fontSize: 14, color: MUTED, marginBottom: 10, lineHeight: 1.65 }}>
        Optional intake supports live assessment capture. Documentation review and pre-submission QA happen in the same flow.
      </div>

      <div
        style={{
          marginBottom: 18,
          padding: 14,
          border: `1px solid ${BORDER}`,
          borderRadius: 14,
          background: "#ffffff"
        }}
      >
        <div style={{ fontWeight: 800, marginBottom: 8, color: TEXT }}>How it works</div>
        <div style={{ fontSize: 14, lineHeight: 1.7, color: "#374151" }}>
          1. Optionally capture findings during assessment
          <br />
          2. Paste or write documentation
          <br />
          3. Add selected OASIS responses if you want pre-submission QA
        </div>
      </div>

      <div style={{ fontSize: 12, color: MUTED, marginBottom: 14, lineHeight: 1.6 }}>
        Pasted text is processed to generate results and is not stored on our servers. Your current
        draft may be temporarily kept in your browser to help protect against accidental loss, and is
        cleared when you sign out or close the tab. Use de-identified documentation only.
      </div>

      <div
        style={{
          marginBottom: 18,
          padding: 16,
          border: `1px solid ${BORDER}`,
          borderRadius: 14,
          background: "#f9fafb"
        }}
      >
        <div style={{ fontWeight: 900, fontSize: 14, color: TEXT, marginBottom: 6 }}>Clinical Use Notice</div>
        <div style={{ fontSize: 13, lineHeight: 1.7, color: "#374151" }}>
          OasisCopilot supports documentation review — it does not replace clinical judgment. All
          generated content, including drafts and copy-ready wording, requires clinician review before
          use. If information is missing, clarify it directly rather than treating it as fact. All
          final clinical and OASIS determinations remain the responsibility of the clinician and
          agency.
        </div>
      </div>

      <div
        style={{
          marginBottom: 18,
          padding: 16,
          border: `1px solid ${BORDER}`,
          borderRadius: 14,
          background: "#ffffff"
        }}
      >
        <div className="rowStack" style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
              <div style={{ fontWeight: 900, color: TEXT }}>During assessment intake (optional)</div>
              {intakeCompletedCount > 0 && (
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: ACCENT_BLUE,
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    borderRadius: 999,
                    padding: "2px 9px"
                  }}
                >
                  {intakeCompletedCount} of {INTAKE_FIELDS.length} added
                </div>
              )}
            </div>
            <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.65 }}>
              Lightweight capture only. Use it if helpful. Skip it if you already have your note.
            </div>
          </div>

          <button
            onClick={() => setShowIntake((prev) => !prev)}
            style={{
              padding: "11px 14px",
              borderRadius: 12,
              cursor: "pointer",
              border: `1px solid ${BORDER}`,
              background: "#fff",
              fontWeight: 800
            }}
          >
            {showIntake ? "Hide intake" : "Show intake"}
          </button>
        </div>

        {showIntake && (
          <div style={{ marginTop: 16 }}>
            <div
              className="intakeGrid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 12
              }}
            >
              {INTAKE_FIELDS.map((field) => (
                <div key={field.key}>
                  <label style={{ fontWeight: 800, display: "block", marginBottom: 6 }}>{field.label}</label>
                  <textarea
                    rows={3}
                    value={intakeFields[field.key]}
                    onChange={(e) => setIntakeField(field.key, e.target.value)}
                    placeholder={field.placeholder}
                    style={{
                      width: "100%",
                      padding: 10,
                      border: `1px solid ${BORDER}`,
                      borderRadius: 12,
                      fontSize: 14,
                      lineHeight: 1.55,
                      resize: "vertical",
                      boxSizing: "border-box"
                    }}
                  />
                </div>
              ))}
            </div>

            {structuredIntakeText && (
              <div
                style={{
                  marginTop: 14,
                  padding: 12,
                  borderRadius: 12,
                  background: "#f9fafb",
                  border: `1px solid ${BORDER}`
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 800, color: "#374151", marginBottom: 6 }}>
                  Intake summary that will be included in review
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.65, color: TEXT, whiteSpace: "pre-wrap" }}>
                  {structuredIntakeText}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <label style={{ fontWeight: 800 }}>Visit Documentation</label>
      <div style={{ fontSize: 13, color: MUTED, margin: "6px 0 10px", lineHeight: 1.65 }}>
        Best for completed visit notes, OASIS support narratives, wound documentation, homebound status, and ADL descriptions.
      </div>

      <div className="rowStack" style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
        <label style={{ fontSize: 13, color: "#374151", flex: "1 1 auto" }}>
          Documentation templates (optional):&nbsp;
          <select
            className="controlSelect"
            value={templateChoice}
            onChange={(e) => handleTemplateChange(e.target.value)}
            style={{ padding: "10px 12px", borderRadius: 12, border: `1px solid ${BORDER}` }}
          >
            {TEMPLATE_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </label>

        <button
          className="fullWidthBtn"
          onClick={insertTemplate}
          style={{ padding: "13px 14px", cursor: "pointer", borderRadius: 12 }}
        >
          Insert Template
        </button>

        <button
          className="fullWidthBtn"
          onClick={clearReview}
          style={{ padding: "13px 14px", cursor: "pointer", borderRadius: 12 }}
        >
          Clear
        </button>
      </div>

      <div style={{ fontSize: 12, color: MUTED, marginBottom: 10 }}>
        Templates are optional and can be edited after insertion.
      </div>

      <textarea
        rows={12}
        style={{
          width: "100%",
          padding: 12,
          border: `1px solid ${BORDER}`,
          borderRadius: 12,
          fontSize: 14,
          lineHeight: 1.65,
          boxSizing: "border-box"
        }}
        placeholder="Paste documentation here…"
        value={notes}
        onChange={(e) => {
          setNotes(e.target.value);
          setResult(null);
          setError("");
          setExpandedIssues({});
          invalidateGeneratedResults();
        }}
      />

      {(showBriefHint || showLongHint) && (
        <div style={{ marginTop: 8, fontSize: 12, color: "#374151", lineHeight: 1.65 }}>
          {showBriefHint && <>Documentation appears brief. Additional detail may improve the completeness of results.</>}
          {showLongHint && <>Large paste detected. If results are slow or incomplete, paste the most relevant portion of the note.</>}
        </div>
      )}

      {isAutoReviewing && (
        <div
          style={{
            marginTop: 8,
            fontSize: 12,
            fontWeight: 700,
            color: ACCENT_BLUE
          }}
        >
          Reviewing…
        </div>
      )}

      <div className="rowStack" style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 14, flexWrap: "wrap" }}>
        <label style={{ fontWeight: 800 }}>Assessment Type</label>
        <select
          className="controlSelect"
          value={assessmentType}
          onChange={(e) => setAssessmentType(e.target.value)}
          style={{ padding: "10px 12px", borderRadius: 12, border: `1px solid ${BORDER}` }}
        >
          <option>Start of Care</option>
          <option>Recertification</option>
          <option>Discharge</option>
        </select>
      </div>

      <label style={{ fontWeight: 800, display: "block", marginTop: 14 }}>
        Selected OASIS Responses (optional)
      </label>
      <div style={{ fontSize: 13, color: MUTED, margin: "6px 0 10px", lineHeight: 1.65 }}>
        Leave blank for documentation support only. Add responses to compare your selections against the documentation before submission.
      </div>

      <textarea
        rows={8}
        style={{
          width: "100%",
          padding: 12,
          border: `1px solid ${BORDER}`,
          borderRadius: 12,
          fontSize: 14,
          lineHeight: 1.65,
          boxSizing: "border-box"
        }}
        value={responsesText}
        onChange={(e) => {
          setResponsesText(e.target.value);
          setResult(null);
          setError("");
          setExpandedIssues({});
        }}
        placeholder={`Examples:
M1860 Independent
M1400: 0 - No shortness of breath
M1850 - Requires assistance or supervision for transfer
"M1400": "1 - When walking more than 20 feet or climbing stairs", "M1860": "2 - Requires use of a two-handed device or supervision or assistance"`}
      />

      <div className="rowStack" style={{ display: "flex", gap: 14, alignItems: "flex-start", marginTop: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <button
            className="fullWidthBtn"
            onClick={() => runReview("manual")}
            disabled={loading || isAutoReviewing || !hasAnyInput}
            style={{
              padding: "13px 16px",
              cursor: loading || isAutoReviewing || !hasAnyInput ? "not-allowed" : "pointer",
              background: ACCENT_BLUE,
              color: "#fff",
              border: "none",
              borderRadius: 12,
              fontWeight: 800,
              minWidth: 220
            }}
          >
            {loading ? "Working…" : "Run Review"}
          </button>
          <div style={{ fontSize: 11.5, color: MUTED, lineHeight: 1.5, maxWidth: 220 }}>
            Find missing, unclear, or unsupported documentation before submission.
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <button
            className="fullWidthBtn"
            onClick={generateDocumentationPackage}
            disabled={generationBusy || !hasAnyInput}
            style={{
              padding: "13px 16px",
              cursor: generationBusy || !hasAnyInput ? "not-allowed" : "pointer",
              background: "#fff",
              color: ACCENT_BLUE,
              border: `1px solid ${ACCENT_BLUE}`,
              borderRadius: 12,
              fontWeight: 800,
              minWidth: 220
            }}
          >
            {docPackageLoading ? "Generating…" : "Generate Complete Documentation"}
          </button>
          <div style={{ fontSize: 11.5, color: MUTED, lineHeight: 1.5, maxWidth: 220 }}>
            Generate a structured documentation draft from the information provided, ready for your review.
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <button
            className="fullWidthBtn"
            onClick={generateNarrative}
            disabled={generationBusy || !hasAnyInput}
            style={{
              padding: "13px 16px",
              cursor: generationBusy || !hasAnyInput ? "not-allowed" : "pointer",
              background: "#fff",
              color: ACCENT_BLUE,
              border: `1px solid ${ACCENT_BLUE}`,
              borderRadius: 12,
              fontWeight: 800,
              minWidth: 200
            }}
          >
            {narrativeLoading ? "Generating…" : "Generate Narrative"}
          </button>
          <div style={{ fontSize: 11.5, color: MUTED, lineHeight: 1.5, maxWidth: 200 }}>
            Generate a narrative draft from the documented information, ready for your review.
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <button
            className="fullWidthBtn"
            onClick={clearReview}
            disabled={loading}
            style={{ padding: "13px 16px", cursor: "pointer", borderRadius: 12 }}
          >
            Clear
          </button>
        </div>
      </div>

      {docPackageError && (
        <div
          style={{
            marginTop: 15,
            padding: 12,
            border: `1px solid ${BORDER}`,
            background: "#f9fafb",
            borderRadius: 12
          }}
        >
          {docPackageError}
        </div>
      )}

      {docPackageResult && !docPackageError && (
        <DocumentationPackagePanel
          result={docPackageResult}
          onCopyText={async (text) => {
            const ok = await copyToClipboard(text);
            setToast(ok ? "Copied." : "Copy failed. Please try again.");
          }}
          accentBlue={ACCENT_BLUE}
          text={TEXT}
          muted={MUTED}
          border={BORDER}
        />
      )}

      {narrativeError && (
        <div
          style={{
            marginTop: 15,
            padding: 12,
            border: `1px solid ${BORDER}`,
            background: "#f9fafb",
            borderRadius: 12
          }}
        >
          {narrativeError}
        </div>
      )}

      {narrativeResult && !narrativeError && (
        <NarrativePanel
          result={narrativeResult}
          onCopyText={async (text) => {
            const ok = await copyToClipboard(text);
            setToast(ok ? "Copied." : "Copy failed. Please try again.");
          }}
          accentBlue={ACCENT_BLUE}
          text={TEXT}
          muted={MUTED}
          border={BORDER}
        />
      )}

      {error && (
        <div
          style={{
            marginTop: 15,
            padding: 12,
            border: `1px solid ${BORDER}`,
            background: "#f9fafb",
            borderRadius: 12
          }}
        >
          {error}
        </div>
      )}

      {result && !error && (
        <UnifiedResultPanel
          result={result}
          expandedIssues={expandedIssues}
          setExpandedIssues={setExpandedIssues}
          accentBlue={ACCENT_BLUE}
          text={TEXT}
          muted={MUTED}
          border={BORDER}
          onCopy={async () => {
            const ok = await copyToClipboard(buildReadableExport(result));
            setToast(ok ? "Copied summary." : "Copy failed. Please try again.");
          }}
          onDownload={() => {
            const safeDate = new Date().toISOString().slice(0, 10);
            downloadTxt(`oasis-review-${safeDate}.txt`, buildReadableExport(result));
            setToast("Downloaded summary.");
          }}
          onRerun={() => {
            setResult(null);
            setError("");
            setExpandedIssues({});
            setToast("Edit documentation, then run review again.");
          }}
          onCopyIssueText={async (text) => {
            const ok = await copyToClipboard(text);
            setToast(ok ? "Copied." : "Copy failed. Please try again.");
          }}
          onInsertIssueFix={insertFixIntoNotes}
          guidedQuestions={guidedQuestions}
          guidedActive={guidedActive}
          guidedSnapshot={guidedSnapshot}
          guidedStepIndex={guidedStepIndex}
          guidedDraft={guidedDraft}
          guidedBusy={loading}
          onGuidedStart={startGuidedQuestions}
          onGuidedDraftChange={updateGuidedDraft}
          onGuidedBack={goGuidedBack}
          onGuidedContinue={continueGuidedQuestions}
          onGuidedCancel={cancelGuidedQuestions}
        />
      )}

      {result && !error && Array.isArray(result.issues) && result.issues.length > 0 && !smartResolutionActive && (
        <div style={{ marginTop: 22 }}>
          <button
            onClick={startSmartResolution}
            style={{
              padding: "13px 16px",
              borderRadius: 12,
              cursor: "pointer",
              background: ACCENT_BLUE,
              color: "#fff",
              border: "none",
              fontWeight: 800
            }}
          >
            Start Smart Resolution
          </button>
        </div>
      )}

      {smartResolutionActive && (
        <SmartResolutionPanel
          queue={resolutionQueue}
          states={resolutionStates}
          drafts={resolutionDrafts}
          corrections={resolutionCorrections}
          details={resolutionDetails}
          activeItem={activeResolutionItem}
          busy={resolutionBusy}
          onSelectIssue={selectResolutionIssue}
          onOptionChange={updateResolutionOptionDraft}
          onFreeTextChange={updateResolutionFreeTextDraft}
          onContinue={continueResolution}
          onCorrectionChange={updateResolutionCorrectionText}
          onInsert={insertResolutionFix}
          onRetry={retryResolution}
          onNext={advanceToNextResolutionIssue}
          onClose={closeSmartResolution}
          accentBlue={ACCENT_BLUE}
          text={TEXT}
          muted={MUTED}
          border={BORDER}
        />
      )}

      {result && !error && (
        <FinalVisitQAGate
          result={result}
          resolutionStates={resolutionStates}
          text={TEXT}
          muted={MUTED}
          border={BORDER}
        />
      )}

      <FooterLinks showAuthedLinks={true} signOut={signOut} accentBlue={ACCENT_BLUE} muted={MUTED} />
    </div>
  );
}

export default function App() {
  return <MainApp />;
}

